import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, Edit3, Palette, BarChart3, Tag, Save, X, Megaphone, FolderTree, EyeOff, Eye, ChefHat, Truck, Snowflake, Sunrise, AlertTriangle, ListChecks, Ban, Settings, Moon, Sun, Sparkles, KeyRound, Tv, Smartphone, CheckCircle2, RefreshCw, ExternalLink, Ticket, Printer, QrCode, Banknote, Users, ShieldCheck, ClipboardCheck, LogOut } from "lucide-react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import TopBar from "./TopBar";
import { getBranding, saveBranding, subscribeBranding } from "../lib/branding";
import { fmtEUR } from "../lib/format";
import { saveAuth, getAuth, clearAuth } from "../lib/auth";

// 4 hoofdtabs zichtbaar bovenaan; rest leeft binnen 'Instellingen'
const TABS = [
  { id: "menu", label: "Menu", icon: Tag },
  { id: "bestellingen", label: "Bestellingen", icon: ListChecks },
  { id: "rapport", label: "Rapport", icon: BarChart3 },
  { id: "kaartjes", label: "Kaartjes", icon: Ticket },
  { id: "instellingen", label: "Instellingen", icon: Settings },
];

export const VOORBEREIDINGSTYPES = [
  { id: "", label: "—", icon: null, color: "#A0A0A0" },
  { id: "live", label: "Live bereid", icon: ChefHat, color: "#EF476F" },
  { id: "vooraf", label: "Vooraf bereid", icon: Snowflake, color: "#118AB2" },
  { id: "extern", label: "Extern geleverd", icon: Truck, color: "#073B4C" },
];

function AdminQuickActions({ teamA, onDagstart, onLogout, onOpenAsTeamA }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-1.5" data-testid="admin-quick-actions">
      <IconBtn onClick={onDagstart} title="Dagstart" testid="dagstart-btn" bg="var(--brand-yellow)" color="var(--ink)">
        <Sunrise className="w-4 h-4" />
      </IconBtn>

      <div className="relative">
        <button
          onClick={() => setOpen((o) => !o)}
          className="btn-press inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-bold text-white"
          style={{ background: teamA?.kleur || "#0E8F2A" }}
          data-testid="open-as-team-btn"
          title="Open Kassa of Voorbereider als Team A"
        >
          <Users className="w-3.5 h-3.5" />
          Open als A
        </button>
        {open && (
          <div
            className="absolute right-0 top-full mt-1 z-40 bg-white rounded-2xl shadow-2xl border-2 border-[var(--neutral)] py-1 min-w-[180px]"
            data-testid="open-as-team-menu"
          >
            <button
              onClick={() => { setOpen(false); onOpenAsTeamA("/kassa"); }}
              className="btn-press w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold hover:bg-[var(--neutral)]"
              data-testid="open-kassa-btn"
            >
              <Banknote className="w-4 h-4" /> Kassa (Team A)
            </button>
            <button
              onClick={() => { setOpen(false); onOpenAsTeamA("/voorbereider"); }}
              className="btn-press w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold hover:bg-[var(--neutral)]"
              data-testid="open-voorbereider-btn"
            >
              <ChefHat className="w-4 h-4" /> Voorbereider (Team A)
            </button>
          </div>
        )}
      </div>

      <a
        href="/live"
        target="_blank"
        rel="noreferrer"
        className="btn-press inline-flex items-center justify-center h-9 w-9 rounded-full bg-[var(--ink)] text-white"
        title="Open Live Dashboard"
        data-testid="open-live-dashboard-btn"
      >
        <Tv className="w-4 h-4" />
      </a>
      <IconBtn onClick={onLogout} title="Uitloggen" testid="admin-logout-btn" bg="#fff" color="var(--ink)" border="var(--neutral)">
        <LogOut className="w-4 h-4" />
      </IconBtn>
    </div>
  );
}

function IconBtn({ onClick, title, testid, bg, color, border, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="btn-press inline-flex items-center justify-center h-9 w-9 rounded-full text-sm font-bold"
      style={{
        background: bg,
        color,
        border: border ? `2px solid ${border}` : "none",
      }}
    >
      {children}
    </button>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("menu");
  const [showDagstart, setShowDagstart] = useState(false);
  const [lastDagstart, setLastDagstart] = useState(null);
  const [teamA, setTeamA] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dagstart").then(({ data }) => setLastDagstart(data?.last_started || null));
    api.get("/teams")
      .then(({ data }) => setTeamA(data.find((t) => t.letter === "A") || null))
      .catch(() => {});
  }, []);

  const runDagstart = async () => {
    try {
      const { data } = await api.post("/dagstart");
      setLastDagstart(data.timestamp);
      setShowDagstart(false);
      toast.success(
        `Dagstart voltooid! ${data.counters_reset} teller(s) gereset, ${data.uitverkocht_reset} product(en) terug op voorraad.`,
        { duration: 5000 }
      );
    } catch {
      toast.error("Dagstart mislukt");
    }
  };

  const adminGoTo = (route) => {
    const auth = getAuth();
    if (!auth || auth.role !== "admin" || !teamA) {
      toast.error("Team A niet beschikbaar");
      return;
    }
    // Admin behoudt zijn admin-token en pakt Team A context (geen slot-claim nodig)
    saveAuth("admin", auth.token, {
      id: teamA.id,
      letter: teamA.letter,
      naam: teamA.naam,
      kleur: teamA.kleur,
    });
    navigate(route);
  };

  return (
    <div className="min-h-screen dot-bg">
      <TopBar title="Admin" subtitle="Dashboard" color="var(--brand-green)">
        <AdminQuickActions
          teamA={teamA}
          onDagstart={() => setShowDagstart(true)}
          onLogout={() => {
            clearAuth();
            navigate("/");
          }}
          onOpenAsTeamA={adminGoTo}
        />
        <div className="flex gap-1.5 flex-wrap" data-testid="admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`btn-press inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  active
                    ? "bg-[var(--brand-green)] text-white border-[var(--brand-green)]"
                    : "bg-white text-[var(--ink)] border-[var(--neutral)]"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </TopBar>

      {showDagstart && (
        <DagstartDialog
          onConfirm={runDagstart}
          onClose={() => setShowDagstart(false)}
          lastDagstart={lastDagstart}
        />
      )}

      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-6">
        {tab === "menu" && <MenuTab />}
        {tab === "bestellingen" && <BestellingenTab />}
        {tab === "rapport" && <RapportTab />}
        {tab === "kaartjes" && <KaartjesTab />}
        {tab === "instellingen" && <InstellingenTab />}
      </div>
    </div>
  );
}

function DagstartDialog({ onConfirm, onClose, lastDagstart }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // focus first interactive element
    const t = setTimeout(() => {
      document.querySelector('[data-testid="dagstart-confirm"]')?.focus();
    }, 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="dagstart-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dagstart-title"
      aria-describedby="dagstart-desc"
    >
      <div
        className="bg-white rounded-3xl p-8 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-[var(--brand-yellow)] flex items-center justify-center" aria-hidden="true">
            <Sunrise className="w-7 h-7 text-[var(--ink)]" />
          </div>
          <h2 id="dagstart-title" className="font-display text-3xl font-extrabold">Dagstart</h2>
        </div>
        <p id="dagstart-desc" className="text-[var(--ink-soft)] mb-4 leading-relaxed">
          Start een nieuwe festivaldag. Dit doet:
        </p>
        <ul className="space-y-2 mb-4 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-[var(--brand-green)] font-bold">✓</span>
            <span>Alle ordernummers terug naar <b>001</b> per team</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[var(--brand-green)] font-bold">✓</span>
            <span>Alle <b>uitverkocht</b>-flags resetten</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[var(--brand-green)] font-bold">✓</span>
            <span>Startmoment loggen voor de rapporten</span>
          </li>
        </ul>
        <div className="rounded-2xl bg-[#FFF4D6] p-3 flex items-start gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-[var(--brand-orange)] flex-shrink-0 mt-0.5" />
          <div className="text-xs text-[var(--ink)] font-medium">
            Eerdere orders blijven bewaard in de database en rapporten. Alleen tellers en voorraadstatus worden gereset.
          </div>
        </div>
        {lastDagstart && (
          <div className="text-xs text-[var(--ink-soft)] mb-4">
            Laatste dagstart: {new Date(lastDagstart).toLocaleString("nl-NL")}
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="btn-press flex-1 px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
            data-testid="dagstart-cancel"
          >
            Annuleer
          </button>
          <button
            onClick={onConfirm}
            className="btn-press flex-1 px-4 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-bold"
            data-testid="dagstart-confirm"
          >
            Start dag
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Menu Tab ----------------
function MenuTab() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    naam: "",
    prijs: "",
    categorie: "",
    emoji: "",
    productnotitie: "",
    voorbereidingstype: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const formRef = useRef(null);

  const load = async () => {
    const [m, c] = await Promise.all([api.get("/menu"), api.get("/categories")]);
    setItems(m.data);
    setCategories(c.data);
  };
  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, []);

  const reset = () => {
    setForm({ naam: "", prijs: "", categorie: "", emoji: "", productnotitie: "", voorbereidingstype: "" });
    setEditingId(null);
  };

  const save = async () => {
    if (!form.naam) {
      toast.error("Naam is verplicht");
      return;
    }
    const payload = {
      naam: form.naam,
      prijs: parseFloat(form.prijs || "0"),
      categorie: form.categorie || "Algemeen",
      emoji: form.emoji || "",
      productnotitie: form.productnotitie || "",
      voorbereidingstype: form.voorbereidingstype || "",
    };
    try {
      if (editingId) {
        await api.put(`/menu/${editingId}`, payload);
        toast.success("Product bijgewerkt");
      } else {
        await api.post("/menu", payload);
        toast.success("Product toegevoegd");
      }
      reset();
      load();
    } catch (e) {
      toast.error("Mislukt om op te slaan");
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      naam: item.naam,
      prijs: String(item.prijs),
      categorie: item.categorie || "",
      emoji: item.emoji || "",
      productnotitie: item.productnotitie || "",
      voorbereidingstype: item.voorbereidingstype || "",
    });
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const remove = async (id) => {
    if (!window.confirm("Verwijder dit product?")) return;
    await api.delete(`/menu/${id}`);
    toast.success("Verwijderd");
    load();
  };

  const toggleUitverkocht = async (item) => {
    await api.put(`/menu/${item.id}`, { uitverkocht: !item.uitverkocht });
    load();
  };

  const toggleZichtbaar = async (item) => {
    await api.put(`/menu/${item.id}`, { zichtbaar: !(item.zichtbaar ?? true) });
    load();
  };

  const filtered = filterCat ? items.filter((i) => i.categorie === filterCat) : items;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-menu-tab">
      <div className="tile p-6 lg:col-span-1 h-fit lg:sticky lg:top-[88px]" ref={formRef}>
        <h2 className="text-xl font-display font-bold mb-1 inline-flex items-center gap-2">
          {editingId ? <><Edit3 className="w-5 h-5" /> Product bewerken</> : <><Plus className="w-5 h-5" /> Nieuw product</>}
        </h2>
        {editingId && (
          <div className="mb-3 -mt-1 px-3 py-1.5 rounded-full bg-[var(--brand-yellow)] text-[var(--ink)] text-xs font-bold inline-flex items-center gap-2" data-testid="menu-editing-banner">
            <Edit3 className="w-3 h-3" /> Bewerken: {form.naam || "(zonder naam)"}
            <button onClick={reset} className="ml-1 underline" data-testid="menu-editing-cancel">Annuleer</button>
          </div>
        )}
        <div className="space-y-3">
          <Field label="Naam">
            <input
              className="input"
              value={form.naam}
              onChange={(e) => setForm({ ...form, naam: e.target.value })}
              placeholder="Friet groot"
              data-testid="menu-form-naam"
            />
          </Field>
          <Field label="Prijs (€) — laat 0 voor 'nog instellen'">
            <input
              type="number"
              step="0.10"
              className="input"
              value={form.prijs}
              onChange={(e) => setForm({ ...form, prijs: e.target.value })}
              placeholder="3.50"
              data-testid="menu-form-prijs"
            />
          </Field>
          <Field label="Categorie">
            <select
              className="input"
              value={form.categorie}
              onChange={(e) => setForm({ ...form, categorie: e.target.value })}
              data-testid="menu-form-categorie"
            >
              <option value="">— Kies categorie —</option>
              {categories.map((c) => (
                <option key={c.id} value={c.naam}>
                  {c.naam}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Voorbereidingstype">
            <select
              className="input"
              value={form.voorbereidingstype}
              onChange={(e) => setForm({ ...form, voorbereidingstype: e.target.value })}
              data-testid="menu-form-voorbereidingstype"
            >
              {VOORBEREIDINGSTYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Productnotitie (voor kassa/keuken)">
            <textarea
              className="input min-h-[60px] resize-none"
              value={form.productnotitie}
              onChange={(e) => setForm({ ...form, productnotitie: e.target.value })}
              placeholder="Bv. 'Bevat noten' of 'Vraag voorkeur'"
              data-testid="menu-form-productnotitie"
            />
          </Field>
          <Field label="Emoji / Symbool (optioneel)">
            <input
              className="input"
              value={form.emoji}
              onChange={(e) => setForm({ ...form, emoji: e.target.value })}
              placeholder="🍟"
              maxLength={4}
              data-testid="menu-form-emoji"
            />
          </Field>
          <div className="flex gap-2 pt-2">
            <button
              onClick={save}
              className="btn-press flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-[var(--success)] text-white font-semibold"
              data-testid="menu-form-save"
            >
              <Save className="w-4 h-4" /> {editingId ? "Opslaan" : "Toevoegen"}
            </button>
            {editingId && (
              <button
                onClick={reset}
                className="btn-press px-4 py-3 rounded-2xl bg-white border-2 border-[#ecebe6] font-semibold"
                data-testid="menu-form-cancel"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-xl font-display font-bold">Producten ({filtered.length}/{items.length})</h2>
          <div className="flex gap-1 flex-wrap" data-testid="menu-cat-filter">
            <button
              onClick={() => setFilterCat("")}
              className={`btn-press px-3 py-1.5 rounded-full text-xs font-semibold border-2 ${
                filterCat === "" ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[#ecebe6]"
              }`}
            >
              Alle
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setFilterCat(c.naam)}
                className={`btn-press px-3 py-1.5 rounded-full text-xs font-semibold border-2 ${
                  filterCat === c.naam
                    ? "text-white border-transparent"
                    : "bg-white border-[#ecebe6]"
                }`}
                style={filterCat === c.naam ? { background: c.kleur } : undefined}
              >
                {c.naam}
              </button>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className="tile p-10 text-center text-[var(--ink-soft)]" data-testid="menu-empty">
            Geen producten in deze filter.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="menu-list">
            {filtered.map((item) => {
              const vbType = VOORBEREIDINGSTYPES.find((t) => t.id === item.voorbereidingstype);
              const verborgen = item.zichtbaar === false;
              return (
                <div
                  key={item.id}
                  className={`tile p-4 flex items-start gap-3 ${verborgen ? "opacity-50" : ""}`}
                  data-testid={`menu-item-${item.id}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-[var(--neutral)] flex items-center justify-center font-display text-2xl flex-shrink-0">
                    {item.emoji || item.naam.slice(0, 1).toUpperCase()}
                  </div>
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => startEdit(item)}
                    data-testid={`menu-row-edit-${item.id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold text-lg truncate">{item.naam}</span>
                      {item.uitverkocht && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--danger)] text-white">
                          Uitverkocht
                        </span>
                      )}
                      {verborgen && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--ink-soft)] text-white">
                          Verborgen
                        </span>
                      )}
                      {vbType && vbType.id && (
                        <span
                          className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full text-white"
                          style={{ background: vbType.color }}
                        >
                          {vbType.label}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[var(--ink-soft)]">
                      {item.prijs > 0 ? fmtEUR(item.prijs) : "Prijs n.v.t."} · {item.categorie}
                    </div>
                    {item.productnotitie && (
                      <div className="text-xs text-[var(--ink-soft)] italic mt-1 line-clamp-1">📝 {item.productnotitie}</div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => toggleZichtbaar(item)}
                      title={verborgen ? "Toon" : "Verberg tijdelijk"}
                      className="btn-press p-2 rounded-xl bg-white border-2 border-[#ecebe6]"
                      data-testid={`menu-toggle-zichtbaar-${item.id}`}
                    >
                      {verborgen ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => toggleUitverkocht(item)}
                      className="btn-press px-2 py-2 rounded-xl bg-white border-2 border-[#ecebe6] text-[10px] font-semibold uppercase tracking-wide"
                      data-testid={`menu-toggle-uitverkocht-${item.id}`}
                    >
                      {item.uitverkocht ? "Voorraad" : "Uitverkocht"}
                    </button>
                    <button
                      onClick={() => startEdit(item)}
                      className="btn-press p-2 rounded-xl bg-white border-2 border-[#ecebe6]"
                      data-testid={`menu-edit-${item.id}`}
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => remove(item.id)}
                      className="btn-press p-2 rounded-xl bg-[var(--danger)] text-white"
                      data-testid={`menu-delete-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Categories Tab ----------------
function CategoriesTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ naam: "", kleur: "#073B4C", volgorde: 0 });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/categories");
    setList(data);
  };
  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, []);

  const reset = () => {
    setForm({ naam: "", kleur: "#073B4C", volgorde: 0 });
    setEditingId(null);
  };

  const save = async () => {
    if (!form.naam) {
      toast.error("Naam is verplicht");
      return;
    }
    const payload = { naam: form.naam, kleur: form.kleur, volgorde: parseInt(form.volgorde) || 0 };
    try {
      if (editingId) {
        await api.put(`/categories/${editingId}`, payload);
        toast.success("Categorie bijgewerkt");
      } else {
        await api.post("/categories", payload);
        toast.success("Categorie toegevoegd");
      }
      reset();
      load();
    } catch {
      toast.error("Opslaan mislukt");
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ naam: c.naam, kleur: c.kleur || "#073B4C", volgorde: c.volgorde || 0 });
  };

  const remove = async (id) => {
    if (!window.confirm("Categorie verwijderen?")) return;
    try {
      await api.delete(`/categories/${id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-categories-tab">
      <div className="tile p-6 h-fit">
        <h2 className="text-xl font-display font-bold mb-4">
          {editingId ? "Categorie bewerken" : "Nieuwe categorie"}
        </h2>
        <div className="space-y-3">
          <Field label="Naam">
            <input
              className="input"
              value={form.naam}
              onChange={(e) => setForm({ ...form, naam: e.target.value })}
              placeholder="WARM ETEN"
              data-testid="cat-form-naam"
            />
          </Field>
          <Field label="Kleur">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.kleur}
                onChange={(e) => setForm({ ...form, kleur: e.target.value })}
                className="h-10 w-16 rounded-xl border-2 border-[#ecebe6] cursor-pointer"
                data-testid="cat-form-kleur"
              />
              <input
                className="input flex-1"
                value={form.kleur}
                onChange={(e) => setForm({ ...form, kleur: e.target.value })}
              />
            </div>
          </Field>
          <Field label="Volgorde">
            <input
              type="number"
              className="input"
              value={form.volgorde}
              onChange={(e) => setForm({ ...form, volgorde: e.target.value })}
              data-testid="cat-form-volgorde"
            />
          </Field>
          <div className="flex gap-2 pt-2">
            <button
              onClick={save}
              className="btn-press flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-[var(--success)] text-white font-semibold"
              data-testid="cat-form-save"
            >
              <Save className="w-4 h-4" /> {editingId ? "Opslaan" : "Toevoegen"}
            </button>
            {editingId && (
              <button
                onClick={reset}
                className="btn-press px-4 py-3 rounded-2xl bg-white border-2 border-[#ecebe6]"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        <h2 className="text-xl font-display font-bold mb-4">Categorieën ({list.length})</h2>
        <div className="space-y-2" data-testid="cat-list">
          {list.map((c) => (
            <div key={c.id} className="tile p-4 flex items-center gap-3" data-testid={`cat-${c.id}`}>
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-display font-bold text-2xl"
                style={{ background: c.kleur }}
              >
                {c.naam.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-lg truncate">{c.naam}</div>
                <div className="text-xs text-[var(--ink-soft)]">Volgorde: {c.volgorde}</div>
              </div>
              <button
                onClick={() => startEdit(c)}
                className="btn-press p-2 rounded-xl bg-white border-2 border-[#ecebe6]"
                data-testid={`cat-edit-${c.id}`}
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => remove(c.id)}
                className="btn-press p-2 rounded-xl bg-[var(--danger)] text-white"
                data-testid={`cat-delete-${c.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Teams Tab ----------------
function TeamsTab() {
  const [list, setList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const { data } = await api.get("/teams/admin");
    setList(data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (team, patch) => {
    try {
      await api.put(`/teams/${team.id}`, patch);
      toast.success(`${team.naam} bijgewerkt`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    }
  };

  const remove = async (team, force = false) => {
    try {
      const url = force ? `/teams/${team.id}?force=true` : `/teams/${team.id}`;
      const { data } = await api.delete(url);
      const extra = data.cancelled_orders ? ` (${data.cancelled_orders} order(s) geannuleerd)` : "";
      toast.success(`${team.naam} verwijderd${extra}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      // Bij open orders → vraag om force
      if (e?.response?.status === 400 && /open order/i.test(detail)) {
        const proceed = window.confirm(
          `${detail}\n\nWil je deze open orders annuleren en het team alsnog verwijderen?`
        );
        if (proceed) {
          await remove(team, true);
        }
        return;
      }
      toast.error(detail || "Verwijderen mislukt");
    }
  };

  const create = async (payload) => {
    try {
      const { data } = await api.post("/teams", payload);
      toast.success(`${data.naam} aangemaakt (letter ${data.letter})`);
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aanmaken mislukt");
    }
  };

  return (
    <div data-testid="admin-teams-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold">
          {list.length} team(s) — admin kan codes wijzigen, teams toevoegen of verwijderen
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-press inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--brand-green)] text-white font-bold text-sm"
          data-testid="team-create-btn"
        >
          <Plus className="w-4 h-4" /> Nieuw team
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((team) => (
          <TeamCard key={team.id} team={team} onSave={save} onDelete={remove} />
        ))}
      </div>

      {showCreate && (
        <TeamCreateDialog onCancel={() => setShowCreate(false)} onCreate={create} />
      )}
    </div>
  );
}

function TeamCreateDialog({ onCancel, onCreate }) {
  const [naam, setNaam] = useState("");
  const [kleur, setKleur] = useState("#0E8F2A");
  const [code, setCode] = useState("");
  const [nummerStart, setNummerStart] = useState("1");

  const submit = () => {
    if (!code || !/^\d{4}$/.test(code)) {
      toast.error("Team-code moet 4 cijfers zijn");
      return;
    }
    const ns = parseInt(nummerStart || "1", 10);
    if (!ns || ns < 1 || ns > 9999) {
      toast.error("Startnummer moet tussen 1 en 9999 liggen");
      return;
    }
    onCreate({ naam: naam || "Nieuw Team", kleur, code, nummer_start: ns });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      data-testid="team-create-dialog"
    >
      <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl font-extrabold">Nieuw team</h3>
          <button onClick={onCancel} className="btn-press w-9 h-9 rounded-full bg-[var(--neutral)] flex items-center justify-center" data-testid="team-create-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <Field label="Naam">
          <input className="input" value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Bv. Team Sushi" data-testid="team-create-naam" />
        </Field>
        <Field label="Kleur">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={kleur}
              onChange={(e) => setKleur(e.target.value)}
              className="h-10 w-16 rounded-xl border-2 border-[var(--neutral)] cursor-pointer"
              data-testid="team-create-kleur"
            />
            <input className="input flex-1" value={kleur} onChange={(e) => setKleur(e.target.value)} />
          </div>
        </Field>
        <Field label="Team-code (4 cijfers)">
          <input
            inputMode="numeric"
            maxLength={4}
            className="input font-display font-bold tabular-nums tracking-widest text-lg"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="1234"
            data-testid="team-create-code"
          />
        </Field>
        <Field label="Startnummer bonnen (optioneel)">
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={9999}
            className="input font-display font-bold tabular-nums text-lg"
            value={nummerStart}
            onChange={(e) => setNummerStart(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="1"
            data-testid="team-create-nummer-start"
          />
          <div className="text-[11px] text-[var(--ink-soft)] mt-1">
            Eerste order krijgt dit nummer. Default = 1.
          </div>
        </Field>
        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className="btn-press flex-1 px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold" data-testid="team-create-cancel">
            Annuleer
          </button>
          <button onClick={submit} className="btn-press flex-1 px-4 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-bold" data-testid="team-create-submit">
            Aanmaken
          </button>
        </div>
      </div>
    </div>
  );
}

function TeamCard({ team, onSave, onDelete }) {
  const [naam, setNaam] = useState(team.naam);
  const [kleur, setKleur] = useState(team.kleur);
  const [code, setCode] = useState(team.code || "");
  const [showCode, setShowCode] = useState(false);
  const [nummerStart, setNummerStart] = useState(String(team.nummer_start ?? 1));
  const [actief, setActief] = useState(team.actief);
  const [terminalProvider, setTerminalProvider] = useState(team.terminal_provider || "");
  const [terminalId, setTerminalId] = useState(team.terminal_id || "");
  const [terminalLabel, setTerminalLabel] = useState(team.terminal_label || "");
  const [printerName, setPrinterName] = useState(team.printer_name || "");
  const [autoPrint, setAutoPrint] = useState(team.auto_print_bon || false);
  const [geluidUrl, setGeluidUrl] = useState(team.geluid_data_url || "");
  const dirty =
    naam !== team.naam ||
    kleur !== team.kleur ||
    code !== (team.code || "") ||
    String(team.nummer_start ?? 1) !== nummerStart ||
    actief !== team.actief ||
    terminalProvider !== (team.terminal_provider || "") ||
    terminalId !== (team.terminal_id || "") ||
    terminalLabel !== (team.terminal_label || "") ||
    printerName !== (team.printer_name || "") ||
    autoPrint !== (team.auto_print_bon || false) ||
    geluidUrl !== (team.geluid_data_url || "");

  const onAudioFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500_000) {
      toast.error("Audio bestand max 500KB");
      return;
    }
    if (!f.type.startsWith("audio/")) {
      toast.error("Alleen audio bestanden (.mp3 / .wav / .ogg)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setGeluidUrl(String(reader.result || ""));
    reader.readAsDataURL(f);
  };

  const previewAudio = () => {
    if (!geluidUrl) return;
    try {
      new Audio(geluidUrl).play();
    } catch {
      toast.error("Kan audio niet afspelen");
    }
  };

  return (
    <div className="tile p-5" data-testid={`team-card-${team.letter}`}>
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-16 h-16 rounded-3xl flex items-center justify-center font-display text-3xl font-bold text-white"
          style={{ background: kleur }}
        >
          {team.letter}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">Team</div>
          <div className="font-display text-xl font-semibold">{team.letter}</div>
        </div>
      </div>
      <Field label="Naam">
        <input className="input" value={naam} onChange={(e) => setNaam(e.target.value)} data-testid={`team-naam-${team.letter}`} />
      </Field>
      <Field label="Kleur">
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={kleur}
            onChange={(e) => setKleur(e.target.value)}
            className="h-10 w-16 rounded-xl border-2 border-[var(--neutral)] cursor-pointer"
            data-testid={`team-kleur-${team.letter}`}
          />
          <input className="input flex-1" value={kleur} onChange={(e) => setKleur(e.target.value)} />
        </div>
      </Field>
      <Field label="Team-code (4 cijfers)">
        <div className="flex items-center gap-2">
          <input
            type={showCode ? "text" : "password"}
            inputMode="numeric"
            maxLength={4}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
            className="input flex-1 font-display font-bold tabular-nums tracking-widest text-lg"
            data-testid={`team-code-${team.letter}`}
            placeholder="••••"
          />
          <button
            type="button"
            onClick={() => setShowCode((s) => !s)}
            className="btn-press p-3 rounded-xl bg-white border-2 border-[var(--neutral)]"
            aria-label={showCode ? "Verberg" : "Toon"}
            data-testid={`team-code-toggle-${team.letter}`}
          >
            {showCode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <div className="text-[11px] text-[var(--ink-soft)] mt-1">
          Medewerkers van dit team gebruiken deze code op het loginscherm.
        </div>
      </Field>

      <Field label="Startnummer bonnen (1-9999)">
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={9999}
          value={nummerStart}
          onChange={(e) => setNummerStart(e.target.value.replace(/\D/g, "").slice(0, 4))}
          className="input font-display font-bold tabular-nums text-lg"
          data-testid={`team-nummer-start-${team.letter}`}
          placeholder="1"
        />
        <div className="text-[11px] text-[var(--ink-soft)] mt-1">
          Volgende order wordt <strong className="tabular-nums">{team.letter}{String(Math.max(1, parseInt(nummerStart||"1",10))).padStart(3,"0")}</strong>.
          Pas dit aan om te matchen met vooraf-gedrukte bonnetjes. Bij opslaan wordt de teller gereset.
        </div>
      </Field>

      <div className="mt-4 pt-4 border-t-2 border-dashed border-[var(--neutral)]">
        <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold mb-3">
          Betaalautomaat
        </div>
        <Field label="Type">
          <select
            className="input"
            value={terminalProvider}
            onChange={(e) => setTerminalProvider(e.target.value)}
            data-testid={`team-terminal-provider-${team.letter}`}
          >
            <option value="">— Geen —</option>
            <option value="sumup">SumUp (Air / Solo)</option>
            <option value="ccv">CCV</option>
            <option value="stripe-terminal">Stripe Terminal</option>
            <option value="anders">Anders</option>
          </select>
        </Field>
        {terminalProvider && (
          <>
            <Field label="Terminal ID / serienummer">
              <input className="input" value={terminalId} onChange={(e) => setTerminalId(e.target.value)} placeholder="bv. TM6PAQK1" data-testid={`team-terminal-id-${team.letter}`} />
            </Field>
            <Field label="Label (voor cashier)">
              <input className="input" value={terminalLabel} onChange={(e) => setTerminalLabel(e.target.value)} placeholder="SumUp Solo #1" data-testid={`team-terminal-label-${team.letter}`} />
            </Field>
          </>
        )}
      </div>

      <div className="mt-4 pt-4 border-t-2 border-dashed border-[var(--neutral)]">
        <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold mb-3">Printer</div>
        <Field label="Printer-naam (optioneel)">
          <input className="input" value={printerName} onChange={(e) => setPrinterName(e.target.value)} placeholder="bv. Star TSP100 #A" data-testid={`team-printer-name-${team.letter}`} />
        </Field>
        <label className="flex items-center gap-2 mt-2">
          <input type="checkbox" checked={autoPrint} onChange={(e) => setAutoPrint(e.target.checked)} className="w-5 h-5" data-testid={`team-auto-print-${team.letter}`} />
          <span className="text-sm font-medium">Automatisch bon printen bij elke order</span>
        </label>
      </div>

      <label className="flex items-center gap-2 mt-4">
        <input type="checkbox" checked={actief} onChange={(e) => setActief(e.target.checked)} className="w-5 h-5" data-testid={`team-actief-${team.letter}`} />
        <span className="text-sm font-medium">Team actief</span>
      </label>

      <div className="mt-4 pt-4 border-t-2 border-dashed border-[var(--neutral)]">
        <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold mb-2">Audio bij Klaar</div>
        <div className="flex items-center gap-2">
          <input type="file" accept="audio/*" onChange={onAudioFile} className="text-xs flex-1" data-testid={`team-audio-input-${team.letter}`} />
          {geluidUrl && (
            <>
              <button type="button" onClick={previewAudio} className="btn-press px-3 py-2 rounded-xl bg-[var(--brand-yellow)] text-[var(--ink)] text-xs font-bold" data-testid={`team-audio-test-${team.letter}`}>
                Test
              </button>
              <button type="button" onClick={() => setGeluidUrl("")} className="btn-press px-3 py-2 rounded-xl bg-[var(--danger)] text-white text-xs font-bold" data-testid={`team-audio-clear-${team.letter}`}>
                ×
              </button>
            </>
          )}
        </div>
        <div className="text-[10px] text-[var(--ink-soft)] mt-1">
          Max 500KB. Leeg = standaard bel-geluid.
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <button
          onClick={() => {
            if (window.confirm(`Team ${team.naam} verwijderen?`)) {
              onDelete(team);
            }
          }}
          className="btn-press inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-[var(--danger)] text-white font-semibold"
          aria-label="Team verwijderen"
          data-testid={`team-delete-${team.letter}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
        <button
          disabled={!dirty}
          onClick={() => {
            if (code && !/^\d{4}$/.test(code)) {
              toast.error("Team-code moet 4 cijfers zijn");
              return;
            }
            const ns = parseInt(nummerStart || "1", 10);
            if (!ns || ns < 1 || ns > 9999) {
              toast.error("Startnummer moet tussen 1 en 9999 liggen");
              return;
            }
            onSave(team, {
              naam,
              kleur,
              ...(code !== (team.code || "") ? { code } : {}),
              ...(ns !== (team.nummer_start ?? 1) ? { nummer_start: ns } : {}),
              actief,
              terminal_provider: terminalProvider,
              terminal_id: terminalId,
              terminal_label: terminalLabel,
              printer_name: printerName,
              auto_print_bon: autoPrint,
              geluid_data_url: geluidUrl,
            });
          }}
          className="btn-press flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`team-save-${team.letter}`}
        >
          <Save className="w-4 h-4" /> Opslaan
        </button>
      </div>
    </div>
  );
}

// ---------------- Sponsors Tab ----------------
function SponsorsTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ naam: "", afbeelding_url: "", link: "", volgorde: 0 });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/sponsors");
    setList(data);
  };
  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, []);

  const reset = () => {
    setForm({ naam: "", afbeelding_url: "", link: "", volgorde: 0 });
    setEditingId(null);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Bestand te groot (max 1MB). Gebruik liever een URL.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, afbeelding_url: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.naam) {
      toast.error("Naam is verplicht");
      return;
    }
    const payload = {
      naam: form.naam,
      afbeelding_url: form.afbeelding_url || "",
      link: form.link || "",
      volgorde: parseInt(form.volgorde) || 0,
    };
    try {
      if (editingId) {
        await api.put(`/sponsors/${editingId}`, payload);
        toast.success("Sponsor bijgewerkt");
      } else {
        await api.post("/sponsors", payload);
        toast.success("Sponsor toegevoegd");
      }
      reset();
      load();
    } catch {
      toast.error("Opslaan mislukt");
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({ naam: s.naam, afbeelding_url: s.afbeelding_url || "", link: s.link || "", volgorde: s.volgorde || 0 });
  };

  const remove = async (id) => {
    if (!window.confirm("Sponsor verwijderen?")) return;
    await api.delete(`/sponsors/${id}`);
    load();
  };

  const toggleActief = async (s) => {
    await api.put(`/sponsors/${s.id}`, { actief: !s.actief });
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-sponsors-tab">
      <div className="tile p-6 lg:col-span-1 h-fit">
        <h2 className="text-xl font-display font-bold mb-2">
          {editingId ? "Sponsor bewerken" : "Nieuwe sponsor"}
        </h2>
        <p className="text-sm text-[var(--ink-soft)] mb-4">
          Sponsors verschijnen als roterende banner onderaan het Pickup-scherm.
        </p>
        <div className="space-y-3">
          <Field label="Naam">
            <input
              className="input"
              value={form.naam}
              onChange={(e) => setForm({ ...form, naam: e.target.value })}
              placeholder="Sponsor naam"
              data-testid="sponsor-form-naam"
            />
          </Field>
          <Field label="Afbeelding URL">
            <input
              className="input"
              value={form.afbeelding_url}
              onChange={(e) => setForm({ ...form, afbeelding_url: e.target.value })}
              placeholder="https://… of upload hieronder"
              data-testid="sponsor-form-url"
            />
          </Field>
          <Field label="…of upload (max 1MB)">
            <input
              type="file"
              accept="image/*"
              onChange={onFile}
              className="text-sm"
              data-testid="sponsor-form-upload"
            />
          </Field>
          {form.afbeelding_url && (
            <div className="rounded-2xl overflow-hidden border-2 border-[#ecebe6] bg-white">
              <img src={form.afbeelding_url} alt="preview" className="w-full h-32 object-contain" data-testid="sponsor-form-preview" />
            </div>
          )}
          <Field label="Link (optioneel)">
            <input
              className="input"
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              placeholder="https://sponsor.nl"
              data-testid="sponsor-form-link"
            />
          </Field>
          <Field label="Volgorde (lager = eerder)">
            <input
              type="number"
              className="input"
              value={form.volgorde}
              onChange={(e) => setForm({ ...form, volgorde: e.target.value })}
              data-testid="sponsor-form-volgorde"
            />
          </Field>
          <div className="flex gap-2 pt-2">
            <button
              onClick={save}
              className="btn-press flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-[var(--success)] text-white font-semibold"
              data-testid="sponsor-form-save"
            >
              <Save className="w-4 h-4" /> {editingId ? "Opslaan" : "Toevoegen"}
            </button>
            {editingId && (
              <button
                onClick={reset}
                className="btn-press px-4 py-3 rounded-2xl bg-white border-2 border-[#ecebe6]"
                data-testid="sponsor-form-cancel"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        <h2 className="text-xl font-display font-bold mb-4">Sponsors ({list.length})</h2>
        {list.length === 0 ? (
          <div className="tile p-10 text-center text-[var(--ink-soft)]" data-testid="sponsors-empty">
            Nog geen sponsors. De Pickup-banner blijft verborgen tot je er één toevoegt.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sponsors-list">
            {list.map((s) => (
              <div key={s.id} className="tile p-3 flex items-center gap-3" data-testid={`sponsor-${s.id}`}>
                <div className="w-24 h-16 rounded-xl bg-[var(--neutral)] flex items-center justify-center overflow-hidden flex-shrink-0">
                  {s.afbeelding_url ? (
                    <img src={s.afbeelding_url} alt={s.naam} className="w-full h-full object-contain" />
                  ) : (
                    <Megaphone className="w-6 h-6 text-[var(--ink-soft)]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-semibold truncate">{s.naam}</span>
                    {!s.actief && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--ink-soft)] text-white">
                        Inactief
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--ink-soft)] truncate">Volgorde: {s.volgorde}</div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => toggleActief(s)}
                    className="btn-press px-2 py-2 rounded-xl bg-white border-2 border-[#ecebe6] text-xs font-semibold"
                    data-testid={`sponsor-toggle-${s.id}`}
                  >
                    {s.actief ? "Aan" : "Uit"}
                  </button>
                  <button
                    onClick={() => startEdit(s)}
                    className="btn-press p-2 rounded-xl bg-white border-2 border-[#ecebe6]"
                    data-testid={`sponsor-edit-${s.id}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(s.id)}
                    className="btn-press p-2 rounded-xl bg-[var(--danger)] text-white"
                    data-testid={`sponsor-delete-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Bestellingen / Correcties Tab ----------------
function BestellingenTab() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("actief");
  const [restoreOrder, setRestoreOrder] = useState(null);
  const [deleteOrder, setDeleteOrder] = useState(null);
  const [bulkDelete, setBulkDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/orders?limit=200");
    setOrders(data);
  };
  useEffect(() => {
    // eslint-disable-next-line
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  const cancel = async (o) => {
    const isStripe = o.betaalmethode === "stripe";
    const msg = isStripe
      ? `Order ${o.ordernummer} annuleren? Er wordt automatisch een Stripe-refund aangevraagd.`
      : `Order ${o.ordernummer} annuleren? Dit verwijdert hem uit de omzet.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/orders/${o.id}/cancel`);
      if (isStripe) {
        if (data.refund?.refunded) {
          toast.success(`Order ${o.ordernummer} geannuleerd + refund ${data.refund.status}`);
        } else {
          toast.warning(`Order ${o.ordernummer} geannuleerd, maar refund mislukt: ${data.refund?.reason || "onbekend"}`);
        }
      } else {
        toast.success(`Order ${o.ordernummer} geannuleerd`);
      }
      load();
    } catch (e) {
      toast.error("Annuleren mislukt");
    }
  };

  const confirmDeleteOne = async () => {
    if (!deleteOrder) return;
    setBusy(true);
    try {
      await api.delete(`/orders/${deleteOrder.id}`);
      toast.success(`Order ${deleteOrder.ordernummer} permanent verwijderd`);
      setDeleteOrder(null);
      load();
    } catch {
      toast.error("Verwijderen mislukt");
    } finally {
      setBusy(false);
    }
  };

  const confirmBulkDelete = async () => {
    const ids = filtered.map((o) => o.id);
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-delete", { ids });
      toast.success(`${data.deleted} order(s) permanent verwijderd`);
      setBulkDelete(false);
      load();
    } catch {
      toast.error("Bulk-verwijderen mislukt");
    } finally {
      setBusy(false);
    }
  };

  const restoreToStatus = async (status) => {
    if (!restoreOrder) return;
    try {
      await api.put(`/orders/${restoreOrder.id}/status`, { status });
      toast.success(`Order ${restoreOrder.ordernummer} hersteld naar ${status}`);
      setRestoreOrder(null);
      load();
    } catch {
      toast.error("Status-wijziging mislukt");
    }
  };

  const filtered = orders.filter((o) => {
    if (filter === "actief") return o.status !== "geannuleerd" && o.status !== "afgerond";
    if (filter === "geannuleerd") return o.status === "geannuleerd";
    if (filter === "afgegeven") return o.status === "afgerond";
    return true;
  });

  const filterLabel = ({ actief: "actieve", afgegeven: "afgeronde", geannuleerd: "geannuleerde", alle: "" }[filter]) || "";

  return (
    <div className="space-y-4" data-testid="admin-bestellingen-tab">
      {restoreOrder && (
        <RestoreStatusDialog order={restoreOrder} onPick={restoreToStatus} onClose={() => setRestoreOrder(null)} />
      )}
      {deleteOrder && (
        <DeleteOrderDialog
          order={deleteOrder}
          busy={busy}
          onConfirm={confirmDeleteOne}
          onClose={() => setDeleteOrder(null)}
        />
      )}
      {bulkDelete && (
        <BulkDeleteDialog
          count={filtered.length}
          filterLabel={filterLabel}
          busy={busy}
          onConfirm={confirmBulkDelete}
          onClose={() => setBulkDelete(false)}
        />
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { id: "actief", label: "Actief" },
          { id: "afgegeven", label: "Afgerond" },
          { id: "geannuleerd", label: "Geannuleerd" },
          { id: "alle", label: "Alle" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`btn-press px-4 py-2 rounded-full text-sm font-bold border-2 ${
              filter === f.id
                ? "bg-[var(--brand-green)] text-white border-[var(--brand-green)]"
                : "bg-white border-[var(--neutral)]"
            }`}
            data-testid={`bestellingen-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        <div className="text-sm text-[var(--ink-soft)] font-semibold">{filtered.length} orders</div>
        {filtered.length > 0 && (
          <button
            onClick={() => setBulkDelete(true)}
            className="btn-press inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[var(--danger)] text-white text-xs font-bold"
            data-testid="bestellingen-bulk-delete-btn"
            title={`Verwijder alle ${filtered.length} zichtbare orders permanent`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Verwijder zichtbare ({filtered.length})
          </button>
        )}
      </div>

      <div className="space-y-2" data-testid="bestellingen-list">
        {filtered.length === 0 && (
          <div className="tile p-10 text-center text-[var(--ink-soft)]">Geen orders in deze filter.</div>
        )}
        {filtered.map((o) => {
          const cancelled = o.status === "geannuleerd";
          return (
            <div
              key={o.id}
              className={`tile p-3 flex items-center gap-3 ${cancelled ? "opacity-60" : ""}`}
              data-testid={`bestelling-${o.ordernummer}`}
            >
              <div
                className="font-display font-extrabold text-2xl px-3 py-1 rounded-xl text-white"
                style={{ background: o.team_kleur }}
              >
                {o.ordernummer}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold uppercase tracking-wide text-[var(--ink-soft)]">
                    {o.team_naam}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold text-white`}
                    style={{
                      background:
                        cancelled ? "#999" :
                        o.status === "nieuw" ? "#F4C400" :
                        o.status === "bezig" ? "#FF8A00" :
                        o.status === "klaar" ? "#0E8F2A" :
                        "#8A8A8A",
                    }}
                  >
                    {o.status}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--neutral)] text-[var(--ink)] font-bold">
                    {o.betaalmethode}
                  </span>
                  {o.refund_id && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--brand-blue)] text-white font-bold">
                      Refund {o.refund_status}
                    </span>
                  )}
                </div>
                <div className="text-sm truncate mt-1">
                  {o.items.map((i, idx) => (
                    <span key={idx} className="mr-3">
                      {i.aantal}× {i.naam}
                    </span>
                  ))}
                </div>
                {o.notitie && (
                  <div className="text-xs italic text-[var(--ink-soft)] mt-1">📝 {o.notitie}</div>
                )}
              </div>
              <div className="font-display font-extrabold text-xl">{fmtEUR(o.totaal)}</div>
              <div className="flex gap-1">
                {!cancelled && (
                  <button
                    onClick={() => cancel(o)}
                    className="btn-press px-3 py-2 rounded-xl bg-white border-2 border-[var(--danger)] text-[var(--danger)] text-xs font-bold inline-flex items-center gap-1"
                    data-testid={`bestelling-cancel-${o.ordernummer}`}
                    title="Annuleer: markeer als geannuleerd (Stripe-refund indien van toepassing)"
                  >
                    <Ban className="w-3.5 h-3.5" /> Annuleer
                  </button>
                )}
                {cancelled && (
                  <button
                    onClick={() => setRestoreOrder(o)}
                    className="btn-press px-3 py-2 rounded-xl bg-white border-2 border-[var(--neutral)] text-xs font-bold"
                    data-testid={`bestelling-restore-${o.ordernummer}`}
                  >
                    Herstel
                  </button>
                )}
                <button
                  onClick={() => setDeleteOrder(o)}
                  className="btn-press px-3 py-2 rounded-xl bg-[var(--danger)] text-white text-xs font-bold inline-flex items-center gap-1"
                  data-testid={`bestelling-delete-${o.ordernummer}`}
                  title="Permanent verwijderen uit database"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Verwijder
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DeleteOrderDialog({ order, busy, onConfirm, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="bestelling-delete-confirm"
    >
      <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border-4 border-[var(--danger)]">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-[var(--danger)] flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-[var(--danger)]">
              Order {order.ordernummer} permanent verwijderen?
            </h3>
            <p className="text-sm text-[var(--ink-soft)] mt-1">
              Dit haalt de order <strong>volledig</strong> uit de database. Anders dan annuleren:
              de order verdwijnt ook uit alle rapporten en kan niet hersteld worden.
            </p>
          </div>
        </div>
        <div className="rounded-2xl bg-[var(--neutral)] px-3 py-2 mb-4 text-sm">
          <strong>{order.team_naam}</strong> · {fmtEUR(order.totaal)} · {order.betaalmethode}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="btn-press flex-1 px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
            data-testid="bestelling-delete-cancel"
          >
            Annuleer
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="btn-press flex-1 px-4 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold disabled:opacity-50"
            data-testid="bestelling-delete-confirm-btn"
          >
            {busy ? "Bezig…" : "Verwijder permanent"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkDeleteDialog({ count, filterLabel, busy, onConfirm, onClose }) {
  const [typed, setTyped] = useState("");
  const ok = typed.trim() === "WIS";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.65)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="bestellingen-bulk-delete-confirm"
    >
      <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border-4 border-[var(--danger)]">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-[var(--danger)] flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-display text-xl font-extrabold text-[var(--danger)]">
              {count} {filterLabel} order(s) permanent verwijderen?
            </h3>
            <p className="text-sm text-[var(--ink-soft)] mt-1">
              <strong>Onomkeerbaar.</strong> De geselecteerde orders verdwijnen volledig uit de
              database, inclusief rapporten. Gebruik dit alleen voor opruimen van test-data.
            </p>
          </div>
        </div>
        <p className="text-sm font-bold mb-2">
          Typ <code className="px-2 py-1 rounded bg-[var(--neutral)] font-mono text-[var(--danger)]">WIS</code> om te bevestigen:
        </p>
        <input
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="WIS"
          className="input mb-3"
          data-testid="bestellingen-bulk-delete-input"
        />
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="btn-press flex-1 px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
            data-testid="bestellingen-bulk-delete-cancel"
          >
            Annuleer
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !ok}
            className="btn-press flex-1 px-4 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="bestellingen-bulk-delete-confirm-btn"
          >
            {busy ? "Bezig…" : `Verwijder ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function RestoreStatusDialog({ order, onPick, onClose }) {
  const options = [
    { id: "maken", label: "Maken", color: "#FF8A00" },
    { id: "klaar", label: "Klaar", color: "#0E8F2A" },
    { id: "afgerond", label: "Afgerond", color: "#8A8A8A" },
  ];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => {
      document.querySelector('[data-testid="restore-to-nieuw"]')?.focus();
    }, 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="restore-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="restore-title"
      aria-describedby="restore-desc"
    >
      <div
        className="bg-white rounded-3xl p-8 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="font-display font-extrabold text-3xl px-3 py-1 rounded-xl text-white"
            style={{ background: order.team_kleur }}
            aria-hidden="true"
          >
            {order.ordernummer}
          </div>
          <div>
            <h2 id="restore-title" className="font-display text-2xl font-extrabold">
              Herstellen
            </h2>
            <p id="restore-desc" className="text-xs text-[var(--ink-soft)]">
              Order {order.ordernummer} — naar welke status?
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {options.map((opt) => (
            <button
              key={opt.id}
              onClick={() => onPick(opt.id)}
              className="btn-press px-4 py-4 rounded-2xl text-white font-bold uppercase tracking-wide"
              style={{ background: opt.color }}
              data-testid={`restore-to-${opt.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={onClose}
          className="btn-press w-full px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
          data-testid="restore-cancel"
        >
          Annuleer
        </button>
      </div>
    </div>
  );
}

// ---------------- Rapport Tab ----------------
function RapportTab() {
  const [summary, setSummary] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const load = async () => {
    const { data } = await api.get(`/reports/summary?date=${date}`);
    setSummary(data);
  };
  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, [date]);

  const exportCSV = () => {
    if (!summary) return;
    const rows = [
      ["Type", "Naam/Letter", "Aantal", "Omzet (€)"],
      ...summary.per_team.map((v) => ["Team", `${v.letter} - ${v.naam}`, v.aantal, v.omzet.toFixed(2)]),
      ...summary.per_product.map((p) => ["Product", p.naam, p.aantal, p.omzet.toFixed(2)]),
      ...summary.per_betaalmethode.map((b) => ["Betaalmethode", b.betaalmethode, b.aantal, b.omzet.toFixed(2)]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rapport-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printPDF = () => {
    window.print();
  };

  if (!summary) return <div className="tile p-8" data-testid="rapport-loading">Laden…</div>;

  return (
    <div className="space-y-6" data-testid="admin-rapport-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">Datum</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="input ml-2"
            data-testid="rapport-date"
          />
        </div>
        <div className="flex-1" />
        <button
          onClick={exportCSV}
          className="btn-press px-4 py-2 rounded-full bg-white border-2 border-[#ecebe6] font-semibold text-sm"
          data-testid="rapport-export-csv"
        >
          Export CSV (Excel)
        </button>
        <button
          onClick={printPDF}
          className="btn-press px-4 py-2 rounded-full bg-[var(--ink)] text-white font-semibold text-sm"
          data-testid="rapport-print-pdf"
        >
          Print / Opslaan als PDF
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI label="Totale omzet" value={fmtEUR(summary.total_omzet)} testid="kpi-omzet" />
        <KPI label="Aantal orders" value={summary.aantal_orders} testid="kpi-aantal" />
        <KPI
          label="Gem. orderwaarde"
          value={fmtEUR(summary.aantal_orders ? summary.total_omzet / summary.aantal_orders : 0)}
          testid="kpi-gemiddeld"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TableCard title="Per team" testid="table-team">
          {summary.per_team.length === 0 ? (
            <Empty />
          ) : (
            summary.per_team.map((v) => (
              <Row key={v.letter} color={v.kleur} label={`${v.letter} – ${v.naam}`} aantal={v.aantal} omzet={v.omzet} />
            ))
          )}
        </TableCard>
        <TableCard title="Per product" testid="table-product">
          {summary.per_product.length === 0 ? (
            <Empty />
          ) : (
            summary.per_product.map((p, i) => (
              <Row key={i} label={p.naam} aantal={p.aantal} omzet={p.omzet} />
            ))
          )}
        </TableCard>
        <TableCard title="Per betaalmethode" testid="table-betaal">
          {summary.per_betaalmethode.length === 0 ? (
            <Empty />
          ) : (
            summary.per_betaalmethode.map((b, i) => (
              <Row key={i} label={b.betaalmethode} aantal={b.aantal} omzet={b.omzet} />
            ))
          )}
        </TableCard>
      </div>
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="tile p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">{label}</div>
      <div className="font-display text-4xl font-bold mt-1">{value}</div>
    </div>
  );
}
function TableCard({ title, children, testid }) {
  return (
    <div className="tile p-5" data-testid={testid}>
      <h3 className="font-display font-bold text-lg mb-3">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ color, label, aantal, omzet }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-[#ecebe6] last:border-0">
      {color && <span className="w-3 h-3 rounded-full" style={{ background: color }} />}
      <span className="flex-1 font-medium truncate">{label}</span>
      <span className="text-sm text-[var(--ink-soft)]">{aantal}×</span>
      <span className="font-display font-semibold">{fmtEUR(omzet)}</span>
    </div>
  );
}
function Empty() {
  return <div className="text-sm text-[var(--ink-soft)] py-4">Geen data voor deze dag.</div>;
}

// ---------------- Kaartjes Tab ----------------
function KaartjesTab() {
  const [teams, setTeams] = useState([]);
  const [letter, setLetter] = useState("A");
  const [start, setStart] = useState(1);
  const [aantal, setAantal] = useState(100);
  const [generated, setGenerated] = useState(null);
  const [brand, setBrand] = useState(getBranding());

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    const unsub = subscribeBranding(setBrand);
    return unsub;
  }, []);

  const team = teams.find((t) => t.letter === letter);

  const generate = () => {
    const n = Math.max(1, Math.min(999, parseInt(aantal, 10) || 0));
    const s = Math.max(1, Math.min(999, parseInt(start, 10) || 1));
    const last = s + n - 1;
    if (last > 999) {
      toast.error("Maximaal nummer 999 per team — verlaag aantal of startnummer");
      return;
    }
    const numbers = Array.from({ length: n }, (_, i) => `${letter}${String(s + i).padStart(3, "0")}`);
    setGenerated({ team, numbers });
    toast.success(`${n} kaartjes klaar — klik Printen`);
  };

  const onPrint = () => window.print();

  return (
    <div className="space-y-6 print:hidden">
      <div className="tile p-6">
        <h2 className="text-xl font-display font-extrabold mb-1 inline-flex items-center gap-2">
          <Ticket className="w-5 h-5" /> Vooraf-genummerde kaartjes
        </h2>
        <p className="text-sm text-[var(--ink-soft)] mb-4">
          Genereer printbare kaartjes per team. 8 kaartjes per A4. Knip langs de stippellijnen.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Team">
            <div className="grid grid-cols-6 gap-1">
              {teams.map((t) => (
                <button
                  key={t.letter}
                  onClick={() => setLetter(t.letter)}
                  className="aspect-square rounded-xl font-display font-extrabold text-white text-lg"
                  style={{
                    background: t.kleur,
                    opacity: letter === t.letter ? 1 : 0.55,
                    transform: letter === t.letter ? "scale(1.05)" : "none",
                  }}
                  data-testid={`kaartjes-team-${t.letter}`}
                >
                  {t.letter}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Startnummer">
            <input type="number" min={1} max={999} value={start} onChange={(e) => setStart(e.target.value)} className="input" data-testid="kaartjes-start" />
          </Field>
          <Field label="Aantal">
            <input type="number" min={1} max={999} value={aantal} onChange={(e) => setAantal(e.target.value)} className="input" data-testid="kaartjes-aantal" />
          </Field>
          <Field label="Bereik">
            <div className="px-3 py-3 rounded-2xl bg-[var(--neutral)] font-display font-bold text-center" data-testid="kaartjes-range">
              {letter}{String(start).padStart(3, "0")} – {letter}{String(Math.max(1, (parseInt(start, 10) || 1) + (parseInt(aantal, 10) || 0) - 1)).padStart(3, "0")}
            </div>
          </Field>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={generate} className="btn-press inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-bold" data-testid="kaartjes-genereer">
            <Sparkles className="w-4 h-4" /> Genereer
          </button>
          {generated && (
            <button onClick={onPrint} className="btn-press inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[var(--ink)] text-white font-bold" data-testid="kaartjes-print">
              <Printer className="w-4 h-4" /> Printen
            </button>
          )}
        </div>
      </div>

      {generated && (
        <div className="tile p-4" data-testid="kaartjes-preview">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2 font-bold">
            Voorbeeld · {generated.numbers.length} kaartjes
          </div>
          <KaartjesGrid generated={generated} brand={brand} preview />
        </div>
      )}

      {generated && <KaartjesPrintable generated={generated} brand={brand} />}
    </div>
  );
}

function KaartjesPrintable({ generated, brand }) {
  const pages = chunkArray(generated.numbers, 8);
  return (
    <div className="hidden print:block" data-testid="kaartjes-printable">
      {pages.map((page, pi) => (
        <div key={pi} className="kaartjes-page">
          <Card8Grid numbers={page} team={generated.team} brand={brand} />
        </div>
      ))}
    </div>
  );
}

function KaartjesGrid({ generated, brand, preview }) {
  const first = generated.numbers.slice(0, 8);
  const more = generated.numbers.length - first.length;
  return (
    <>
      <Card8Grid numbers={first} team={generated.team} brand={brand} />
      {preview && more > 0 && (
        <div className="text-center text-[var(--ink-soft)] mt-3 text-sm">
          + {more} kaartjes ({Math.ceil(generated.numbers.length / 8)} A4-pagina&apos;s totaal)
        </div>
      )}
    </>
  );
}

function Card8Grid({ numbers, team, brand }) {
  const cells = [...numbers];
  while (cells.length < 8) cells.push(null);
  return (
    <div className="grid grid-cols-2 grid-rows-4 gap-3 kaartjes-grid">
      {cells.map((num, i) => (
        <div
          key={i}
          className="kaartje rounded-2xl flex flex-col items-center justify-center text-center p-3"
          style={{
            border: `2px dashed ${team?.kleur || "#999"}`,
            background: num ? "#fff" : "transparent",
            minHeight: "80mm",
            opacity: num ? 1 : 0.15,
          }}
        >
          {num && (
            <>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: team?.kleur || "#000" }}>
                {team?.naam || `Team ${num[0]}`}
              </div>
              <div
                className="font-display font-extrabold leading-none my-2"
                style={{
                  fontSize: "clamp(3rem, 9vw, 5.5rem)",
                  color: team?.kleur || "#000",
                }}
              >
                {num}
              </div>
              <div className="text-[9px] text-[var(--ink-soft)] tracking-wider">
                {brand?.bedrijfsnaam || "QDB AI SYSTEEM"}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function chunkArray(arr, n) {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}


// ---------------- Setup Tab (Festival Readiness Check) ----------------
function SetupTab() {
  const [brand, setBrand] = useState(getBranding());
  const [teams, setTeams] = useState([]);
  const [menu, setMenu] = useState([]);
  const [sumupCfg, setSumupCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualChecks, setManualChecks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("setup_manual") || "{}");
    } catch {
      return {};
    }
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const [t, m, s] = await Promise.all([
        api.get("/teams"),
        api.get("/menu"),
        api.get("/payments/sumup/config").catch(() => ({ data: null })),
      ]);
      setTeams(t.data);
      setMenu(m.data);
      setSumupCfg(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const unsub = subscribeBranding(setBrand);
    return unsub;
  }, []);

  const toggleManual = (key) => {
    const next = { ...manualChecks, [key]: !manualChecks[key] };
    setManualChecks(next);
    localStorage.setItem("setup_manual", JSON.stringify(next));
  };

  const actieveTeams = teams.filter((t) => t.actief).length;
  const menuMetPrijs = menu.filter((m) => m.zichtbaar !== false && m.prijs > 0).length;
  const audioPerTeam = teams.filter((t) => t.actief && t.geluid_data_url).length;
  const branding = brand?.bedrijfsnaam && brand.bedrijfsnaam !== "QDB AI SYSTEEM";
  const sumupAppswitch = !!sumupCfg?.appswitch_available;

  const checks = [
    {
      key: "teams",
      label: "Minimaal 3 actieve teams",
      ok: actieveTeams >= 3,
      hint: `${actieveTeams} team(s) actief`,
      tip: "Admin → Instellingen → Teams → activeer of voeg teams toe",
    },
    {
      key: "menu",
      label: "Menu heeft producten met prijs",
      ok: menuMetPrijs >= 5,
      hint: `${menuMetPrijs} producten met prijs`,
      tip: "Admin → Menu → voeg producten toe en stel prijs in",
    },
    {
      key: "branding",
      label: "Bedrijfsnaam aangepast",
      ok: branding,
      hint: branding ? brand.bedrijfsnaam : "Nog default 'QDB AI SYSTEEM'",
      tip: "Admin → Instellingen → Branding → wijzig bedrijfsnaam",
    },
    {
      key: "sumup",
      label: "SumUp betaling actief",
      ok: sumupAppswitch,
      hint: sumupAppswitch ? "App-switch actief" : "Niet geconfigureerd",
      tip: "Vul SUMUP_AFFILIATE_KEY in /app/backend/.env",
    },
    {
      key: "audio",
      label: "Audio per team geüpload (optioneel)",
      ok: audioPerTeam >= 1,
      hint: audioPerTeam > 0 ? `${audioPerTeam}/${actieveTeams} teams met geluid` : "Standaard bel gebruikt",
      tip: "Admin → Teams → upload .mp3 per team",
      optional: true,
    },
    {
      key: "codes_changed",
      label: "Team-codes & admin-PIN gewijzigd vóór festival",
      ok: !!manualChecks.codes_changed,
      hint: manualChecks.codes_changed ? "Bevestigd" : "Bevestig handmatig",
      tip: "Admin → Teams → wijzig codes · Instellingen → wijzig admin-PIN",
      manual: true,
    },
    {
      key: "kaartjes_geprint",
      label: "Kaartjes geprint voor alle teams",
      ok: !!manualChecks.kaartjes_geprint,
      hint: manualChecks.kaartjes_geprint ? "Bevestigd" : "Bevestig handmatig",
      tip: "Admin → Kaartjes tab → genereer + print per team",
      manual: true,
    },
    {
      key: "telefoons_onboarded",
      label: "Telefoons gekoppeld aan slots",
      ok: !!manualChecks.telefoons_onboarded,
      hint: manualChecks.telefoons_onboarded ? "Bevestigd" : "Bevestig handmatig",
      tip: "Admin → Instellingen → QR-code scannen, elke telefoon kiest team-code + rol",
      manual: true,
    },
    {
      key: "sumup_paired",
      label: "SumUp Solo gekoppeld aan kassa-telefoons",
      ok: !!manualChecks.sumup_paired,
      hint: manualChecks.sumup_paired ? "Bevestigd" : "Bevestig handmatig",
      tip: "SumUp-app → Card readers → + Add reader → set default",
      manual: true,
      optional: true,
    },
  ];

  const required = checks.filter((c) => !c.optional);
  const requiredOk = required.filter((c) => c.ok).length;
  const ready = requiredOk === required.length;

  return (
    <div className="space-y-4" data-testid="admin-setup-tab">
      <div className="tile p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-display font-extrabold inline-flex items-center gap-2">
              <ListChecks className="w-5 h-5" /> Festival Readiness Check
            </h2>
            <p className="text-sm text-[var(--ink-soft)] mt-1">
              Loop deze lijst af op de ochtend van het evenement. Verplichte checks moeten allemaal groen zijn.
            </p>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="btn-press inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-[var(--ink)] text-white text-sm font-bold disabled:opacity-50"
            data-testid="setup-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Verversen
          </button>
        </div>

        <div
          className={`mt-5 p-5 rounded-2xl flex items-center gap-4 ${
            ready ? "bg-green-50 border-2 border-[var(--success)]" : "bg-yellow-50 border-2 border-yellow-400"
          }`}
          data-testid="setup-readiness-badge"
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center text-white font-display font-extrabold text-2xl"
            style={{ background: ready ? "var(--success)" : "#facc15" }}
          >
            {requiredOk}/{required.length}
          </div>
          <div className="flex-1">
            <div className="font-display text-2xl font-extrabold">
              {ready ? "Klaar voor opening! 🎉" : "Nog wat te doen…"}
            </div>
            <div className="text-sm text-[var(--ink-soft)]">
              {ready
                ? "Alle verplichte checks staan groen. Veel succes!"
                : `${required.length - requiredOk} verplichte check(s) nog niet afgevinkt.`}
            </div>
          </div>
        </div>
      </div>

      <div className="tile p-2 md:p-4">
        <ul className="divide-y divide-[#ecebe6]">
          {checks.map((c) => (
            <li
              key={c.key}
              className="flex items-center gap-3 px-3 py-4"
              data-testid={`setup-check-${c.key}`}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: c.ok ? "var(--success)" : c.optional ? "var(--neutral)" : "#fef3c7",
                  color: c.ok ? "#fff" : c.optional ? "#666" : "#92400e",
                }}
              >
                {c.ok ? <CheckCircle2 className="w-5 h-5" /> : <X className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold inline-flex items-center gap-2">
                  {c.label}
                  {c.optional && (
                    <span className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)] px-2 py-0.5 rounded bg-[var(--neutral)]">
                      Optioneel
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--ink-soft)] mt-0.5">
                  <strong>{c.hint}</strong> · {c.tip}
                </div>
              </div>
              {c.manual && (
                <button
                  onClick={() => toggleManual(c.key)}
                  className="btn-press px-3 py-2 rounded-xl text-xs font-bold flex-shrink-0"
                  style={{
                    background: c.ok ? "var(--neutral)" : "var(--brand-yellow)",
                    color: "var(--ink)",
                  }}
                  data-testid={`setup-toggle-${c.key}`}
                >
                  {c.ok ? "Reset" : "Vink af"}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <FestivalResetCard onAfterReset={refresh} />
      <WipeOrdersCard onAfterWipe={refresh} />
    </div>
  );
}

function WipeOrdersCard({ onAfterWipe }) {
  const [busy, setBusy] = useState(false);
  const [confirmStep, setConfirmStep] = useState(0); // 0 closed, 1 first warn, 2 type-WIS
  const [typed, setTyped] = useState("");
  const [lastResult, setLastResult] = useState(null);

  const doWipe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wipe-orders");
      setLastResult(data);
      setConfirmStep(0);
      setTyped("");
      toast.success(
        `Schoongemaakt: ${data.orders_deleted} order(s), ${data.payments_deleted} transactie(s), ${data.locks_released} slot(s)`,
        { duration: 6000 }
      );
      onAfterWipe?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="tile p-6 border-2 border-[var(--danger)]" data-testid="wipe-orders-card">
        <h2 className="text-xl font-display font-extrabold mb-2 inline-flex items-center gap-2 text-[var(--danger)]">
          <AlertTriangle className="w-5 h-5" /> Test-data opruimen
        </h2>
        <p className="text-sm text-[var(--ink-soft)] mb-4 max-w-2xl">
          Wist <strong>ALLE</strong> bestellingen, betaal-transacties, order-tellers en device-locks.
          Handig om de app voor het echte festival vers te starten ná test-rondes.
          <br /><br />
          <strong className="text-[var(--ink)]">Blijven bewaard:</strong> teams, codes, admin-PIN, menu, branding, sponsors, categorieën.
        </p>
        <button
          onClick={() => setConfirmStep(1)}
          className="btn-press inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold"
          data-testid="wipe-orders-btn"
        >
          <Trash2 className="w-4 h-4" /> Reset alles naar nul
        </button>
        {lastResult && (
          <div className="text-xs text-[var(--ink-soft)] mt-3" data-testid="wipe-orders-last">
            Laatst opgeschoond: {new Date(lastResult.timestamp).toLocaleString("nl-NL")} ·{" "}
            {lastResult.orders_deleted} orders, {lastResult.payments_deleted} transacties verwijderd
          </div>
        )}
      </div>

      {confirmStep > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
          style={{ background: "rgba(0,0,0,0.65)" }}
          onClick={(e) => e.target === e.currentTarget && setConfirmStep(0)}
          data-testid="wipe-orders-confirm"
        >
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border-4 border-[var(--danger)]">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl bg-[var(--danger)] flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-display text-xl font-extrabold text-[var(--danger)]">
                  Pas op — kan niet ongedaan!
                </h3>
                <p className="text-sm text-[var(--ink-soft)] mt-1">
                  Alle bestellingen, transacties en order-tellers worden permanent verwijderd.
                </p>
              </div>
            </div>

            {confirmStep === 1 && (
              <div className="flex gap-2 mt-4" data-testid="wipe-step-1">
                <button
                  onClick={() => {
                    setConfirmStep(0);
                    setTyped("");
                  }}
                  className="btn-press flex-1 px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
                  data-testid="wipe-cancel-1"
                >
                  Annuleer
                </button>
                <button
                  onClick={() => setConfirmStep(2)}
                  className="btn-press flex-1 px-4 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold"
                  data-testid="wipe-continue"
                >
                  Doorgaan
                </button>
              </div>
            )}

            {confirmStep === 2 && (
              <div data-testid="wipe-step-2">
                <p className="text-sm font-bold mb-2">
                  Typ <code className="px-2 py-1 rounded bg-[var(--neutral)] font-mono text-[var(--danger)]">WIS ALLES</code> om te bevestigen:
                </p>
                <input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  className="input mb-3"
                  placeholder="WIS ALLES"
                  autoFocus
                  data-testid="wipe-confirm-input"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setConfirmStep(0);
                      setTyped("");
                    }}
                    className="btn-press flex-1 px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
                    data-testid="wipe-cancel-2"
                  >
                    Annuleer
                  </button>
                  <button
                    onClick={doWipe}
                    disabled={busy || typed.trim() !== "WIS ALLES"}
                    className="btn-press flex-1 px-4 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="wipe-final-btn"
                  >
                    {busy ? "Bezig…" : "Verwijder permanent"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function FestivalResetCard({ onAfterReset }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [brand, setBrand] = useState(getBranding());

  useEffect(() => subscribeBranding(setBrand), []);

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/teams/regenerate-codes");
      setResult(data);
      setModalOpen(true);
      setConfirm(false);
      toast.success(`${data.count} team-codes geregenereerd. Print de sheet voor je bemanning!`);
      onAfterReset?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenereren mislukt");
    } finally {
      setBusy(false);
    }
  };

  const printSheet = () => {
    window.print();
  };

  return (
    <>
      <div className="tile p-6" data-testid="festival-reset-card">
        <h2 className="text-xl font-display font-extrabold mb-2 inline-flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[var(--brand-yellow)]" /> Festival reset
        </h2>
        <p className="text-sm text-[var(--ink-soft)] mb-4 max-w-2xl">
          In één klik random nieuwe team-codes genereren én een printbare A4 maken om aan je bemanning te geven.
          Handig voor een nieuwe festival-dag of nieuw evenement, om "code-leak" van vorige edities te voorkomen.
          Admin-PIN en order-tellers blijven onaangetast.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setConfirm(true)}
            className="btn-press inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[var(--brand-yellow)] text-[var(--ink)] font-bold"
            data-testid="festival-reset-btn"
          >
            <RefreshCw className="w-4 h-4" /> Genereer nieuwe team-codes
          </button>
          {result && (
            <button
              onClick={() => setModalOpen(true)}
              className="btn-press inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[var(--ink)] text-white font-bold"
              data-testid="festival-reset-show-last"
            >
              <Eye className="w-4 h-4" /> Toon laatste codes
            </button>
          )}
        </div>
        {result && (
          <div className="mt-4 text-xs text-[var(--ink-soft)]">
            Laatst geregenereerd: {new Date(result.regenerated_at).toLocaleString("nl-NL")}
          </div>
        )}
      </div>

      {confirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
          style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={(e) => e.target === e.currentTarget && setConfirm(false)}
          data-testid="festival-reset-confirm"
        >
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl bg-[var(--brand-yellow)] flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-[var(--ink)]" />
              </div>
              <div>
                <h3 className="font-display text-xl font-extrabold">Zeker weten?</h3>
                <p className="text-sm text-[var(--ink-soft)] mt-1">
                  Alle bestaande team-codes worden vervangen door nieuwe random codes. Medewerkers met de oude codes kunnen niet meer inloggen.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setConfirm(false)}
                className="btn-press flex-1 px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
                data-testid="festival-reset-cancel"
              >
                Annuleer
              </button>
              <button
                onClick={doReset}
                disabled={busy}
                className="btn-press flex-1 px-4 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold disabled:opacity-50"
                data-testid="festival-reset-confirm-btn"
              >
                {busy ? "Bezig…" : "Genereer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {result && modalOpen && !confirm && (
        <FestivalResetResultModal
          result={result}
          brand={brand}
          onClose={() => setModalOpen(false)}
          onPrint={printSheet}
        />
      )}

      {result && <FestivalCodesPrintable result={result} brand={brand} />}
    </>
  );
}

function FestivalResetResultModal({ result, brand, onClose, onPrint }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center p-0 md:p-4 animate-fade-in print:hidden"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="festival-reset-result-modal"
    >
      <div className="bg-white w-full md:max-w-3xl md:rounded-3xl flex flex-col max-h-[95vh] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b-2 border-[#ecebe6]">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold">
              Nieuwe team-codes
            </div>
            <div className="font-display text-2xl font-extrabold inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[var(--brand-yellow)]" /> {brand.bedrijfsnaam || "QDB AI SYSTEEM"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn-press w-11 h-11 rounded-full bg-[var(--neutral)] flex items-center justify-center"
            data-testid="festival-result-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {result.teams.map((t) => (
              <div
                key={t.id}
                className="rounded-2xl p-4 text-white flex flex-col items-center"
                style={{ background: t.kleur }}
                data-testid={`festival-code-${t.letter}`}
              >
                <div className="text-xs uppercase tracking-[0.3em] font-bold opacity-90">
                  {t.naam}
                </div>
                <div className="font-display font-extrabold leading-none my-2 select-all" style={{ fontSize: "clamp(3rem,8vw,4.5rem)" }}>
                  {t.code}
                </div>
                <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">
                  Team {t.letter}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 p-4 rounded-2xl bg-[var(--neutral)] text-sm">
            <strong>Belangrijk:</strong> bewaar deze pagina voor je crew. Druk op <em>Printen</em> voor een net A4-overzicht
            dat je in het opbouw-momentje kunt uitdelen.
          </div>
        </div>
        <div className="border-t-2 border-[#ecebe6] p-4 flex flex-wrap gap-2 justify-end">
          <button
            onClick={onClose}
            className="btn-press px-5 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
            data-testid="festival-result-dismiss"
          >
            Sluiten
          </button>
          <button
            onClick={onPrint}
            className="btn-press inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-bold"
            data-testid="festival-result-print"
          >
            <Printer className="w-4 h-4" /> Printen (A4)
          </button>
        </div>
      </div>
    </div>
  );
}

function FestivalCodesPrintable({ result, brand }) {
  return (
    <div className="hidden print:block" data-testid="festival-codes-printable">
      <div className="kaartjes-page">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--ink-soft)]">
            {brand.bedrijfsondertitel || "Festival POS"}
          </div>
          <div className="font-display text-4xl font-extrabold mt-1">
            {brand.bedrijfsnaam || "QDB AI SYSTEEM"} — Team-codes
          </div>
          <div className="text-sm text-[var(--ink-soft)] mt-1">
            Geldig vanaf {new Date(result.regenerated_at).toLocaleString("nl-NL")}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6">
          {result.teams.map((t) => (
            <div
              key={t.id}
              className="rounded-2xl p-6 text-center text-white"
              style={{ background: t.kleur, minHeight: "55mm" }}
            >
              <div className="text-xs uppercase tracking-[0.3em] font-bold opacity-90">
                Team {t.letter}
              </div>
              <div className="font-display font-extrabold leading-none my-3" style={{ fontSize: "5rem" }}>
                {t.code}
              </div>
              <div className="text-sm font-bold opacity-90">{t.naam}</div>
            </div>
          ))}
        </div>
        <div className="text-center text-[10px] text-[var(--ink-soft)] mt-8 italic">
          Vouw of knip dit blad en geef de juiste code aan elke teamleider. Codes zijn 4 cijfers; medewerkers kiezen na inloggen hun rol (Kassa of Voorbereider).
        </div>
      </div>
    </div>
  );
}


// ---------------- Instellingen Tab ----------------
function InstellingenTab() {
  const [section, setSection] = useState("branding");
  const SECTIONS = [
    { id: "branding", label: "Branding", icon: Sparkles },
    { id: "teams", label: "Teams", icon: Users },
    { id: "categorieen", label: "Categorieën", icon: FolderTree },
    { id: "sponsors", label: "Sponsors", icon: Megaphone },
    { id: "beveiliging", label: "Beveiliging", icon: ShieldCheck },
    { id: "setup", label: "Setup-check", icon: ClipboardCheck },
  ];

  return (
    <div data-testid="admin-instellingen-tab">
      <div className="flex gap-2 flex-wrap mb-5 tile p-3" data-testid="instellingen-subtabs">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = section === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`btn-press inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border-2 ${
                active
                  ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                  : "bg-white text-[var(--ink)] border-[var(--neutral)]"
              }`}
              data-testid={`instellingen-sub-${s.id}`}
            >
              <Icon className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>

      {section === "branding" && <BrandingSection />}
      {section === "teams" && <TeamsTab />}
      {section === "categorieen" && <CategoriesTab />}
      {section === "sponsors" && <SponsorsTab />}
      {section === "beveiliging" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PinManagementCard />
          <SumUpStatusCard />
          <SumUpReadersCard />
          <LocksOverviewCard />
          <CrewQrCard />
          <SetupQRCard />
        </div>
      )}
      {section === "setup" && <SetupTab />}
    </div>
  );
}

function BrandingSection() {
  const [brand, setBrand] = useState(getBranding());
  const [form, setForm] = useState(getBranding());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = subscribeBranding((b) => {
      setBrand(b);
      setForm(b);
    });
    return unsub;
  }, []);

  const dirty = JSON.stringify(form) !== JSON.stringify(brand);

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo te groot (max 500KB). Verklein eerst de afbeelding.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, logo_url: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveBranding(form);
      toast.success("Branding opgeslagen");
    } catch {
      toast.error("Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tile p-6 max-w-2xl">
      <h2 className="text-xl font-display font-extrabold mb-4 inline-flex items-center gap-2">
        <Sparkles className="w-5 h-5" /> Branding
      </h2>

      <Field label="Bedrijfsnaam">
        <input
          className="input"
          value={form.bedrijfsnaam}
          onChange={(e) => setForm({ ...form, bedrijfsnaam: e.target.value })}
          data-testid="brand-bedrijfsnaam"
        />
      </Field>
      <Field label="Ondertitel (op login scherm)">
        <input
          className="input"
          value={form.bedrijfsondertitel}
          onChange={(e) => setForm({ ...form, bedrijfsondertitel: e.target.value })}
          data-testid="brand-ondertitel"
        />
      </Field>

      <Field label="Logo (max 500KB)">
        <input
          type="file"
          accept="image/*"
          onChange={onLogoUpload}
          className="text-sm"
          data-testid="brand-logo-upload"
        />
      </Field>
      {form.logo_url && (
        <div className="rounded-2xl bg-[var(--bg-soft)] border-2 border-[var(--neutral)] p-3 mt-2 flex items-center justify-between">
          <img src={form.logo_url} alt="logo" className="max-h-16 object-contain" data-testid="brand-logo-preview" />
          <button
            onClick={() => setForm({ ...form, logo_url: "" })}
            className="btn-press p-2 rounded-xl bg-white border-2 border-[var(--neutral)]"
            data-testid="brand-logo-remove"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mt-3">
        <Field label="Hoofdkleur">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.hoofdkleur}
              onChange={(e) => setForm({ ...form, hoofdkleur: e.target.value })}
              className="h-10 w-16 rounded-xl border-2 border-[var(--neutral)] cursor-pointer"
              data-testid="brand-hoofdkleur"
            />
            <input
              className="input flex-1"
              value={form.hoofdkleur}
              onChange={(e) => setForm({ ...form, hoofdkleur: e.target.value })}
            />
          </div>
        </Field>
        <Field label="Accentkleur">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.accentkleur}
              onChange={(e) => setForm({ ...form, accentkleur: e.target.value })}
              className="h-10 w-16 rounded-xl border-2 border-[var(--neutral)] cursor-pointer"
              data-testid="brand-accentkleur"
            />
            <input
              className="input flex-1"
              value={form.accentkleur}
              onChange={(e) => setForm({ ...form, accentkleur: e.target.value })}
            />
          </div>
        </Field>
      </div>

      <Field label="Thema">
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: "licht", label: "Licht", icon: Sun },
            { id: "donker", label: "Donker", icon: Moon },
            { id: "glass", label: "Glass", icon: Sparkles },
          ].map((t) => {
            const Icon = t.icon;
            const active = form.thema === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setForm({ ...form, thema: t.id })}
                className={`btn-press py-3 rounded-2xl border-2 font-bold inline-flex flex-col items-center gap-1 ${
                  active
                    ? "bg-[var(--brand-green)] text-white border-[var(--brand-green)]"
                    : "bg-white border-[var(--neutral)]"
                }`}
                data-testid={`brand-thema-${t.id}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs">{t.label}</span>
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Bonnen / printer">
        <label className="flex items-center gap-3 px-4 py-3 rounded-2xl border-2 border-[var(--neutral)] cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.bonnen_uit}
            onChange={(e) => setForm({ ...form, bonnen_uit: e.target.checked })}
            className="w-5 h-5"
            data-testid="brand-bonnen-uit"
          />
          <div>
            <div className="font-bold text-sm">Bonnen uitschakelen voor dit evenement</div>
            <div className="text-xs text-[var(--ink-soft)]">
              Verbergt de &quot;Bon printen&quot; checkbox in de kassa en negeert auto-print per team. Printer-instellingen blijven bewaard.
            </div>
          </div>
        </label>
      </Field>

      <button
        onClick={save}
        disabled={!dirty || saving}
        className="btn-press w-full mt-4 px-4 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-bold disabled:opacity-50"
        data-testid="brand-save"
      >
        <Save className="w-4 h-4 inline mr-2" />
        Branding opslaan
      </button>
    </div>
  );
}

function LocksOverviewCard() {
  const [locks, setLocks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [l, t] = await Promise.all([api.get("/locks"), api.get("/teams")]);
      setLocks(l.data);
      setTeams(t.data);
    } catch {
      // ignore
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const reset = async (lock) => {
    if (!window.confirm(`Reset ${lock.resource_id} koppeling?`)) return;
    try {
      await api.delete(`/locks/${lock.resource_type}/${lock.resource_id}`);
      toast.success("Slot vrijgegeven");
      load();
    } catch {
      toast.error("Reset mislukt");
    }
  };

  const resetAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/locks/reset-all");
      toast.success(`${data.count || 0} telefoon-aanmelding(en) gewist`);
      setConfirmAll(false);
      load();
    } catch {
      toast.error("Reset mislukt");
    } finally {
      setBusy(false);
    }
  };

  const ROLES = [
    { id: "kassa", label: "Kassa", color: "#0E8F2A" },
    { id: "voorbereider", label: "Voorb.", color: "#118AB2" },
  ];

  const activeLocks = locks.filter((l) => l.resource_type === "slot").length;

  const formatAge = (iso) => {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "net nu";
    if (mins < 60) return `${mins} min geleden`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}u ${mins % 60}m geleden`;
  };

  return (
    <div className="tile p-6 lg:col-span-2" data-testid="locks-overview-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <h2 className="text-xl font-display font-extrabold inline-flex items-center gap-2">
          <KeyRound className="w-5 h-5" /> Slot-koppelingen (Device locks)
        </h2>
        {activeLocks > 0 && (
          <button
            onClick={() => setConfirmAll(true)}
            disabled={busy}
            className="btn-press inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-[var(--danger)] text-white text-sm font-bold disabled:opacity-50"
            data-testid="locks-reset-all-btn"
          >
            <Trash2 className="w-4 h-4" /> Reset alle telefoons ({activeLocks})
          </button>
        )}
      </div>
      <p className="text-sm text-[var(--ink-soft)] mb-4">
        Elk team heeft 2 slots: Kassa en Voorbereider. Slechts één apparaat per slot. Admin kan
        een slot resetten of alles in één keer wissen — alle telefoons moeten dan opnieuw inloggen.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="slots-grid">
        {teams.map((team) => (
          <div
            key={team.letter}
            className="rounded-2xl border-2 p-3"
            style={{ borderColor: team.kleur + "55" }}
            data-testid={`slot-team-${team.letter}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center font-display font-extrabold text-white text-sm"
                style={{ background: team.kleur }}
              >
                {team.letter}
              </div>
              <div className="font-display font-bold text-sm truncate">{team.naam}</div>
            </div>
            {ROLES.map((r) => {
              const slotId = `${team.letter}_${r.id}`;
              const lock = locks.find((l) => l.resource_type === "slot" && l.resource_id === slotId);
              return (
                <div
                  key={r.id}
                  className="flex items-center gap-2 py-1.5 border-t border-[#ecebe6]"
                  data-testid={`slot-row-${slotId}`}
                >
                  <span
                    className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold text-white"
                    style={{ background: r.color }}
                  >
                    {r.label}
                  </span>
                  <div className="flex-1 min-w-0 text-xs">
                    {lock ? (
                      <>
                        <div className="font-bold truncate" data-testid={`slot-device-${slotId}`}>
                          {lock.label || "Onbekend"}
                        </div>
                        <div className="text-[var(--ink-soft)] text-[10px] truncate">
                          {formatAge(lock.locked_at)} · <span className="font-mono">{lock.device_id.slice(0, 8)}…</span>
                        </div>
                      </>
                    ) : (
                      <span className="text-[var(--ink-soft)] italic">Vrij</span>
                    )}
                  </div>
                  {lock && (
                    <button
                      onClick={() => reset(lock)}
                      className="btn-press px-2 py-1 rounded-lg bg-[var(--danger)] text-white text-[10px] font-bold"
                      data-testid={`slot-reset-${slotId}`}
                    >
                      Reset
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {confirmAll && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
          style={{ background: "rgba(0,0,0,0.65)" }}
          onClick={(e) => e.target === e.currentTarget && setConfirmAll(false)}
          data-testid="locks-reset-all-confirm"
        >
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border-4 border-[var(--danger)]">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl bg-[var(--danger)] flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-display text-xl font-extrabold text-[var(--danger)]">
                  Alle telefoons uitloggen?
                </h3>
                <p className="text-sm text-[var(--ink-soft)] mt-1">
                  Alle <strong>{activeLocks}</strong> actieve aanmelding(en) worden gewist. Elke
                  telefoon moet opnieuw inloggen met team-code en rol-keuze.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setConfirmAll(false)}
                className="btn-press flex-1 px-4 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
                data-testid="locks-reset-all-cancel"
              >
                Annuleer
              </button>
              <button
                onClick={resetAll}
                disabled={busy}
                className="btn-press flex-1 px-4 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold disabled:opacity-50"
                data-testid="locks-reset-all-confirm-btn"
              >
                {busy ? "Bezig…" : "Reset alles"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SetupQRCard() {
  const url = window.location.origin;
  return (
    <div className="tile p-6 lg:col-span-2" data-testid="setup-qr-card">
      <h2 className="text-xl font-display font-extrabold mb-1 inline-flex items-center gap-2">
        <QrCode className="w-5 h-5" /> Telefoons snel onboarden
      </h2>
      <p className="text-sm text-[var(--ink-soft)] mb-4">
        Toon deze QR-code op je laptop. Iedere medewerker scant met de
        camera van zijn telefoon → opent direct de app → Add to Home Screen.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div
          className="bg-white p-4 rounded-2xl border-4 border-[var(--ink)] flex items-center justify-center"
          data-testid="setup-qr-image"
        >
          <QRCode value={url} size={220} level="M" />
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold mb-1">URL</div>
            <div className="font-mono text-sm bg-[var(--neutral)] px-3 py-2 rounded-xl break-all" data-testid="setup-url">
              {url}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold mb-1">Codes</div>
            <ul className="text-sm space-y-1">
              <li><strong>Admin-PIN</strong> · jouw eigenaar / overzicht-PIN (default 1996)</li>
              <li><strong>Team A–F codes</strong> · standaard 1111–6666 — elk team kiest Kassa óf Voorbereider rol</li>
              <li>Meer teams aanmaken kan via <strong>Admin → Instellingen → Teams → Nieuw team</strong></li>
            </ul>
            <div className="text-[11px] text-[var(--ink-soft)] mt-1 italic">
              Verander deze codes vóór het evenement via Admin → Teams en Admin → Instellingen.
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold mb-1">Op telefoon installeren</div>
            <ol className="text-sm space-y-1 list-decimal list-inside">
              <li><strong>iPhone</strong> (Safari): Deel-icoon → <em>Zet op beginscherm</em></li>
              <li><strong>Android</strong> (Chrome): Menu (⋮) → <em>App installeren</em> of <em>Toev. aan startscherm</em></li>
            </ol>
            <div className="text-[11px] text-[var(--ink-soft)] mt-1 italic">
              Werkt dan als een gewone app, ook fullscreen.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PinManagementCard() {
  const [form, setForm] = useState({
    new_admin_pin: "",
    current_admin_pin: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.new_admin_pin || !form.current_admin_pin) {
      toast.error("Vul beide velden in");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/admin-pin", {
        new_admin_pin: form.new_admin_pin,
        current_admin_pin: form.current_admin_pin,
      });
      toast.success("Admin-PIN bijgewerkt");
      setForm({ new_admin_pin: "", current_admin_pin: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Admin-PIN wijziging mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tile p-6" data-testid="pin-management-card">
      <h2 className="text-xl font-display font-extrabold mb-4 inline-flex items-center gap-2">
        <KeyRound className="w-5 h-5" /> Admin-PIN beheren
      </h2>
      <p className="text-sm text-[var(--ink-soft)] mb-4">
        Team-codes beheer je bij <strong>Admin → Teams</strong>. Hier wijzig je alleen jouw
        admin-PIN (4 cijfers, mag niet gelijk zijn aan een team-code).
      </p>

      <Field label="Huidige admin-PIN">
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          className="input"
          placeholder="••••"
          value={form.current_admin_pin}
          onChange={(e) => setForm({ ...form, current_admin_pin: e.target.value.replace(/\D/g, "") })}
          data-testid="pin-admin-current"
        />
      </Field>
      <Field label="Nieuwe admin-PIN">
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          className="input"
          placeholder="••••"
          value={form.new_admin_pin}
          onChange={(e) => setForm({ ...form, new_admin_pin: e.target.value.replace(/\D/g, "") })}
          data-testid="pin-admin-new"
        />
      </Field>

      <button
        onClick={save}
        disabled={saving}
        className="btn-press w-full mt-2 px-4 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-bold disabled:opacity-50"
        data-testid="pin-save"
      >
        <Save className="w-4 h-4 inline mr-2" />
        Admin-PIN opslaan
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-1 font-bold">{label}</div>
      {children}
    </label>
  );
}

function SumUpStatusCard() {
  const [cfg, setCfg] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [loadingTerminals, setLoadingTerminals] = useState(false);
  const [scopeError, setScopeError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/payments/sumup/config");
      setCfg(data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, []);

  const loadTerminals = async () => {
    setLoadingTerminals(true);
    setScopeError(null);
    try {
      const { data } = await api.get("/payments/sumup/terminals");
      setTerminals(data.items || []);
      toast.success(`${(data.items || []).length} terminal(s) gevonden`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Ophalen mislukt";
      setScopeError(msg);
      toast.error("SumUp terminals niet beschikbaar");
    } finally {
      setLoadingTerminals(false);
    }
  };

  if (!cfg) {
    return (
      <div className="tile p-6 lg:col-span-2">
        <div className="text-sm text-[var(--ink-soft)]">SumUp status laden…</div>
      </div>
    );
  }

  return (
    <div className="tile p-6 lg:col-span-2" data-testid="sumup-status-card">
      <h2 className="text-xl font-display font-extrabold mb-4 inline-flex items-center gap-2">
        <Smartphone className="w-5 h-5" /> SumUp integratie status
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Badge
          ok={cfg.appswitch_available}
          label="Tap-to-Pay App"
          desc="App-switch flow"
          testid="sumup-badge-appswitch"
        />
        <Badge
          ok={cfg.oauth_configured}
          label="OAuth"
          desc="Client credentials"
          testid="sumup-badge-oauth"
        />
        <Badge
          ok={cfg.transaction_verify_available}
          label="Verificatie"
          desc="transactions.history"
          testid="sumup-badge-tx"
        />
        <Badge
          ok={cfg.solo_available}
          label="Solo terminal"
          desc="readers + payments"
          testid="sumup-badge-solo"
        />
      </div>

      <div className="text-xs text-[var(--ink-soft)] mb-2">
        Merchant: <span className="font-bold text-[var(--ink)]">{cfg.merchant_code || "—"}</span>
        {cfg.granted_scopes && (
          <> · Granted scopes: <span className="font-mono text-xs">{cfg.granted_scopes}</span></>
        )}
      </div>

      {!cfg.solo_available && cfg.oauth_configured && cfg.missing_scopes_for_solo?.length > 0 && (
        <div
          className="mt-3 p-4 rounded-2xl border-2 border-[var(--brand-yellow)] bg-yellow-50/50"
          data-testid="sumup-missing-scopes"
        >
          <div className="font-bold inline-flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-yellow-700" /> Solo terminal nog niet actief
          </div>
          <p className="text-sm mb-2">
            Voeg deze permissions toe aan je SumUp OAuth-app:
          </p>
          <ul className="text-sm font-mono space-y-1 mb-3">
            {cfg.missing_scopes_for_solo.map((s) => (
              <li key={s} className="px-2 py-1 bg-white rounded">• {s}</li>
            ))}
          </ul>
          <ol className="text-sm space-y-1 list-decimal list-inside mb-3">
            <li>Ga naar <a href="https://developer.sumup.com/" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">developer.sumup.com <ExternalLink className="w-3 h-3" /></a></li>
            <li>Open &quot;Your apps&quot; → klik op &quot;QDB AI SYSTEEM Solo&quot;</li>
            <li>Onder &quot;Permissions&quot; / &quot;Scopes&quot;: vink bovenstaande aan</li>
            <li>Save → klik hieronder Verversen</li>
          </ol>
          <button
            onClick={load}
            className="btn-press inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--ink)] text-white text-sm font-bold"
            data-testid="sumup-config-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Verversen
          </button>
        </div>
      )}

      {cfg.solo_available && (
        <div className="mt-3">
          <button
            onClick={loadTerminals}
            disabled={loadingTerminals}
            className="btn-press inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-[var(--brand-green)] text-white font-bold disabled:opacity-50"
            data-testid="sumup-load-terminals"
          >
            <RefreshCw className={`w-4 h-4 ${loadingTerminals ? "animate-spin" : ""}`} />
            Terminals ophalen
          </button>

          {scopeError && (
            <div className="mt-2 text-sm text-[var(--danger)]">{scopeError}</div>
          )}

          {terminals.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="sumup-terminals-list">
              <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold">
                Gevonden terminals
              </div>
              {terminals.map((t) => (
                <div key={t.id || t.serial_number} className="px-3 py-2 rounded-xl border-2 border-[var(--neutral)] flex items-center justify-between">
                  <div>
                    <div className="font-bold">{t.name || t.serial_number || t.id}</div>
                    <div className="text-xs text-[var(--ink-soft)] font-mono">{t.id || t.serial_number}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-[var(--neutral)] uppercase tracking-widest font-bold">
                    {t.status || t.device_model || "ready"}
                  </span>
                </div>
              ))}
              <div className="text-xs text-[var(--ink-soft)] mt-1">
                Koppel een terminal via Teams → Terminal ID veld.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Badge({ ok, label, desc, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl p-3 border-2"
      style={{
        borderColor: ok ? "var(--success)" : "var(--neutral)",
        background: ok ? "rgba(14,143,42,0.05)" : "transparent",
      }}
    >
      <div className="flex items-center gap-2">
        {ok ? (
          <CheckCircle2 className="w-4 h-4 text-[var(--success)]" />
        ) : (
          <X className="w-4 h-4 text-[var(--ink-soft)]" />
        )}
        <div className="font-bold text-sm">{label}</div>
      </div>
      <div className="text-xs text-[var(--ink-soft)] mt-1">{desc}</div>
    </div>
  );
}


// ---------------- Crew QR Card ----------------
function CrewQrCard() {
  const [teams, setTeams] = useState([]);
  const [showSheet, setShowSheet] = useState(false);
  const [brand, setBrand] = useState(getBranding());
  const [revealed, setRevealed] = useState({});

  useEffect(() => {
    const unsub = subscribeBranding(setBrand);
    load();
    return unsub;
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/teams/admin");
      setTeams(data.filter((t) => t.actief !== false));
    } catch {}
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const downloadQR = (team) => {
    const svgEl = document.querySelector(`[data-testid="crew-qr-preview-${team.letter}"] svg`);
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svgEl);
    // Wrap in geletterd kader voor knip
    const wrapped = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="400" height="500" viewBox="0 0 400 500">
  <rect width="400" height="500" rx="24" fill="${team.kleur}"/>
  <text x="200" y="60" font-family="sans-serif" font-size="20" font-weight="800" fill="white" text-anchor="middle" letter-spacing="3">${team.naam.toUpperCase()}</text>
  <g transform="translate(56,90) scale(1.43)">${svgStr.replace(/<\?xml.*?\?>/, "")}</g>
  <text x="200" y="450" font-family="sans-serif" font-size="48" font-weight="900" fill="white" text-anchor="middle">${team.code}</text>
  <text x="200" y="480" font-family="sans-serif" font-size="10" fill="white" text-anchor="middle" letter-spacing="2">SCAN OF TYP CODE</text>
</svg>`;
    const blob = new Blob([wrapped], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-team-${team.letter}-${team.code}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="tile p-6 lg:col-span-2" data-testid="crew-qr-card">
      <h2 className="text-xl font-display font-extrabold mb-1 inline-flex items-center gap-2">
        <QrCode className="w-5 h-5" /> Crew QR-codes
      </h2>
      <p className="text-sm text-[var(--ink-soft)] mb-4 max-w-2xl">
        Crew scant team-QR met telefoon-camera → code wordt automatisch ingevuld → kies rol.
        Print A4 sheet of download losse QR's per team.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 mb-4">
        {teams.map((t) => {
          const url = `${origin}/?code=${t.code}`;
          const open = !!revealed[t.id];
          return (
            <div
              key={t.id}
              className="rounded-2xl p-3 border-2 flex flex-col items-center text-center"
              style={{ borderColor: t.kleur + "55" }}
              data-testid={`crew-qr-preview-${t.letter}`}
            >
              <div
                className="w-full text-white font-bold text-xs uppercase tracking-widest rounded-xl px-2 py-1 mb-2"
                style={{ background: t.kleur }}
              >
                {t.naam}
              </div>
              <div className="bg-white p-2 rounded-xl border border-[#ecebe6]">
                <QRCode value={url} size={96} bgColor="#FFFFFF" fgColor="#0B0B0F" />
              </div>
              <div className="flex gap-1 mt-2">
                <button
                  onClick={() => setRevealed((r) => ({ ...r, [t.id]: !open }))}
                  className="btn-press inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[var(--ink-soft)]"
                  data-testid={`crew-qr-toggle-${t.letter}`}
                >
                  {open ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {open ? "verberg" : "code"}
                </button>
                <span className="text-[10px] text-[var(--ink-soft)]">·</span>
                <button
                  onClick={() => downloadQR(t)}
                  className="btn-press inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[var(--brand-green)]"
                  data-testid={`crew-qr-download-${t.letter}`}
                  title="Download losse QR (SVG)"
                >
                  ⬇ download
                </button>
              </div>
              {open && (
                <div className="font-display font-extrabold text-xl mt-1 tabular-nums" data-testid={`crew-qr-code-${t.letter}`}>
                  {t.code}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowSheet(true)}
          className="btn-press inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-bold"
          data-testid="crew-qr-print-btn"
        >
          <Printer className="w-4 h-4" /> Print A4 sheet (alles)
        </button>
      </div>

      {showSheet && (
        <CrewQrSheetModal
          teams={teams}
          brand={brand}
          origin={origin}
          onClose={() => setShowSheet(false)}
        />
      )}

      <CrewQrPrintable teams={teams} brand={brand} origin={origin} />
    </div>
  );
}

function CrewQrSheetModal({ teams, brand, origin, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center p-0 md:p-4 animate-fade-in print:hidden"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="crew-qr-sheet-modal"
    >
      <div className="bg-white w-full md:max-w-3xl md:rounded-3xl flex flex-col max-h-[95vh] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b-2 border-[#ecebe6]">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold">
              QR-codes voor crew
            </div>
            <div className="font-display text-2xl font-extrabold inline-flex items-center gap-2">
              <QrCode className="w-5 h-5" /> {brand.bedrijfsnaam || "QDB AI SYSTEEM"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn-press w-11 h-11 rounded-full bg-[var(--neutral)] flex items-center justify-center"
            data-testid="crew-qr-sheet-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {teams.map((t) => (
              <div
                key={t.id}
                className="rounded-2xl p-4 text-center text-white flex flex-col items-center"
                style={{ background: t.kleur }}
              >
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-90">
                  {t.naam}
                </div>
                <div className="bg-white p-2 rounded-xl mt-2">
                  <QRCode value={`${origin}/?code=${t.code}`} size={120} bgColor="#FFFFFF" fgColor="#0B0B0F" />
                </div>
                <div className="font-display font-extrabold text-2xl mt-2 tabular-nums">
                  {t.code}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 p-4 rounded-2xl bg-[var(--neutral)] text-sm">
            <strong>Hoe gebruiken:</strong> Crewlid opent telefoon-camera, scant de QR, login-scherm
            opent met de juiste code al ingevuld. Tikken op Kassa of Voorbereider en ze zijn binnen.
          </div>
        </div>
        <div className="border-t-2 border-[#ecebe6] p-4 flex flex-wrap gap-2 justify-end">
          <button
            onClick={onClose}
            className="btn-press px-5 py-3 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
            data-testid="crew-qr-sheet-cancel"
          >
            Sluiten
          </button>
          <button
            onClick={() => window.print()}
            className="btn-press inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[var(--brand-green)] text-white font-bold"
            data-testid="crew-qr-sheet-print"
          >
            <Printer className="w-4 h-4" /> Printen (A4)
          </button>
        </div>
      </div>
    </div>
  );
}

function CrewQrPrintable({ teams, brand, origin }) {
  return (
    <div className="hidden print:block" data-testid="crew-qr-printable">
      <div className="kaartjes-page">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--ink-soft)]">
            Crew QR-codes
          </div>
          <div className="font-display text-3xl font-extrabold mt-1">
            {brand.bedrijfsnaam || "QDB AI SYSTEEM"}
          </div>
          <div className="text-xs text-[var(--ink-soft)] mt-1">
            Scan met telefoon-camera om automatisch in te loggen
          </div>
        </div>
        <div className="grid grid-cols-2 gap-5">
          {teams.map((t) => (
            <div
              key={t.id}
              className="rounded-2xl p-4 text-center text-white flex flex-col items-center"
              style={{ background: t.kleur, minHeight: "85mm" }}
            >
              <div className="text-xs uppercase tracking-[0.3em] font-bold opacity-90">
                {t.naam}
              </div>
              <div className="bg-white p-3 rounded-2xl mt-3">
                <QRCode value={`${origin}/?code=${t.code}`} size={160} bgColor="#FFFFFF" fgColor="#0B0B0F" />
              </div>
              <div className="font-display font-extrabold text-3xl mt-3 tabular-nums">
                {t.code}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- SumUp Readers Card ----------------
function SumUpReadersCard() {
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, t] = await Promise.all([
        api.get("/payments/sumup/terminals"),
        api.get("/teams"),
      ]);
      setItems(r.data?.items || []);
      setTeams(t.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Ophalen mislukt");
    } finally {
      setLastFetch(new Date());
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // poll elke 30s
    return () => clearInterval(id);
  }, []);

  return (
    <div className="tile p-6 lg:col-span-2" data-testid="sumup-readers-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-display font-extrabold inline-flex items-center gap-2">
          <Smartphone className="w-5 h-5" /> SumUp Solo readers
        </h2>
        <button
          onClick={load}
          disabled={loading}
          className="btn-press inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border-2 border-[var(--neutral)] text-xs font-bold disabled:opacity-50"
          data-testid="sumup-readers-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Ververs
        </button>
      </div>

      {error && (
        <div className="rounded-2xl bg-[#FFF4D6] border-2 border-[var(--brand-yellow)] p-3 text-sm" data-testid="sumup-readers-error">
          <strong>Niet beschikbaar:</strong> {error}
          <div className="text-xs text-[var(--ink-soft)] mt-1">
            Activeer scope <code className="font-mono">readers.read</code> + <code className="font-mono">readers.write</code> in je SumUp Developer-portal.
          </div>
        </div>
      )}

      {!error && items.length === 0 && !loading && (
        <div className="text-sm text-[var(--ink-soft)] py-6 text-center" data-testid="sumup-readers-empty">
          Geen Solo readers gevonden. Pair eerst een reader via de SumUp-app op de kassa-telefoon.
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2" data-testid="sumup-readers-list">
          {items.map((r) => {
            const linkedTeam = teams.find(
              (t) => t.terminal_id && (t.terminal_id === r.id || t.terminal_id === r.serial_number)
            );
            const status = (r.status || "unknown").toLowerCase();
            const statusOk = status === "paired" || status === "ready" || status === "active";
            return (
              <div
                key={r.id || r.serial_number}
                className="flex items-center gap-3 p-3 rounded-2xl border-2 border-[var(--neutral)]"
                data-testid={`sumup-reader-${r.id || r.serial_number}`}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: statusOk ? "var(--success)" : "var(--danger)" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-bold inline-flex items-center gap-2">
                    {r.name || r.label || "Solo terminal"}
                    {linkedTeam && (
                      <span
                        className="px-2 py-0.5 rounded text-[10px] uppercase tracking-widest text-white font-bold"
                        style={{ background: linkedTeam.kleur }}
                        data-testid={`sumup-reader-linked-team-${r.id || r.serial_number}`}
                      >
                        {linkedTeam.naam}
                      </span>
                    )}
                    {!linkedTeam && (
                      <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-widest bg-[var(--neutral)] font-bold">
                        niet gekoppeld
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--ink-soft)] font-mono">
                    {r.id || r.serial_number} · {status}
                    {r.battery_level != null && ` · 🔋 ${r.battery_level}%`}
                    {r.last_activity_at && ` · laatst: ${new Date(r.last_activity_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lastFetch && (
        <div className="text-[10px] text-[var(--ink-soft)] text-right mt-3" data-testid="sumup-readers-lastfetch">
          Laatst opgehaald: {lastFetch.toLocaleTimeString("nl-NL")}
        </div>
      )}
    </div>
  );
}
